import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { linkifyText } from "@/lib/linkify";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Plus, Clock, MapPin, User, Tag,
  Pencil, Trash2, AlertTriangle, Phone, ExternalLink, Route, Coins, Send, Mail, Loader2, Hourglass, CalendarPlus,
  Building2, Briefcase, Hash, MessageSquare, CheckCircle2, ChevronDown, Wallet, Users,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TagChip, TagDot } from "@/components/TagChip";
import StatusHistory from "@/components/StatusHistory";
import { formatBookingStamp } from "@/lib/dates";
import BookingChat from "@/components/BookingChat";
import PaymentBadge from "@/components/PaymentBadge";
import EnquiryDialog from "@/components/EnquiryDialog";
import QuickBookingForm from "@/components/QuickBookingForm";
import PendingEnquiriesPanel from "@/components/PendingEnquiriesPanel";
import DayDetailDrawer from "@/components/DayDetailDrawer";
import ConflictWarningDialog from "@/components/ConflictWarningDialog";
import { buildMapsUrl } from "@/lib/mapsUrl";
import { PageRefreshButton } from "@/components/PageRefreshButton";
import { DetailsPendingDot } from "@/components/DetailsPendingBadge";
// Phase P.12 — Redesigned calendar view components.
import CalendarDayView from "@/components/calendar/DayView";
import CalendarWeekView from "@/components/calendar/WeekView";
import CalendarMonthView from "@/components/calendar/MonthView";

// Phase P.3 Item 1 — compact chip label for the calendar weekly tile.
// Handles the "multiple staff" edge case where staff_name arrives as a
// comma-separated list (legacy) or the newer array. First name only when
// there's one person; "N+ staff" when >2 to keep the chip short.
function staffChipLabel(staffName) {
  if (!staffName) return "";
  const parts = String(staffName).split(/[,;]/).map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 1) {
    // First token only (splits on space to strip surname/initial).
    return parts[0] ? parts[0].split(/\s+/)[0] : "";
  }
  if (parts.length === 2) {
    return `${parts[0].split(/\s+/)[0]}, ${parts[1].split(/\s+/)[0]}`;
  }
  return `${parts.length}+ staff`;
}

const STAFF_COLORS = [
  "bg-amber-500",
  "bg-sky-600",
  "bg-emerald-600",
  "bg-rose-600",
  "bg-violet-600",
  "bg-teal-600",
];

function staffColor(staffId) {
  if (!staffId) return STAFF_COLORS[0];
  let hash = 0;
  for (const ch of staffId) hash = (hash * 31 + ch.charCodeAt(0)) | 0;
  return STAFF_COLORS[Math.abs(hash) % STAFF_COLORS.length];
}

function formatTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  } catch {
    return "—";
  }
}

function formatDayHeader(d) {
  return d.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" });
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function PaymentChip({ type, status }) {
  const tlabel = {
    simple_cash: "Cash",
    cash_with_invoice: "Cash + Inv",
    vat: "VAT",
    rct: "RCT",
  }[type] || type;
  const slabel = {
    awaiting_payment: "Awaiting",
    paid: "Paid",
    overdue: "Overdue",
    write_off: "Write-off",
    unable_to_collect: "Uncollectible",
    pending_manager_approval: "Pending approval",
  }[status] || status;
  const sclass = {
    paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
    overdue: "bg-rose-100 text-rose-800 border-rose-200",
    awaiting_payment: "bg-amber-100 text-amber-900 border-amber-200",
    write_off: "bg-zinc-200 text-zinc-700 border-zinc-300",
    unable_to_collect: "bg-zinc-200 text-zinc-700 border-zinc-300",
    pending_manager_approval: "bg-amber-200 text-amber-900 border-amber-400",
  }[status] || "bg-zinc-100";
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="mono text-[10px] uppercase tracking-widest border border-border px-1.5 py-0.5 rounded">
        {tlabel}
      </span>
      <span className={`mono text-[10px] uppercase tracking-widest border px-1.5 py-0.5 rounded ${sclass}`}>
        {slabel}
      </span>
    </div>
  );
}

function staffColorVar(staffId) {
  // Returns a tailwind class (text/bg) for the dot AND a hex-like CSS for the left border.
  if (!staffId) return { cls: STAFF_COLORS[0], hex: "rgb(245 158 11)" };
  let hash = 0;
  for (const ch of staffId) hash = (hash * 31 + ch.charCodeAt(0)) | 0;
  const idx = Math.abs(hash) % STAFF_COLORS.length;
  const palette = [
    "rgb(245 158 11)", // amber-500
    "rgb(2 132 199)",  // sky-600
    "rgb(5 150 105)",  // emerald-600
    "rgb(225 29 72)",  // rose-600
    "rgb(124 58 237)", // violet-600
    "rgb(13 148 136)", // teal-600
  ];
  return { cls: STAFF_COLORS[idx], hex: palette[idx] };
}

function staffFirstName(name) {
  if (!name) return "—";
  return name.split(" ")[0];
}

// Phase 45 — initials chip for the new compact 7-day tile + day-drawer.
function staffInitials(name) {
  if (!name) return "—";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0].toUpperCase())
    .slice(0, 2)
    .join("");
}

// Phase 45 — gradient class for day headers. Past = slate, Today = indigo
// → fuchsia (matches the StaffPortal palette so manager + staff stay in
// visual sync), Future = emerald → teal. Used by both the 7-day desktop
// columns and the mobile vertical list.
function dayHeaderGradient(date, today) {
  const isToday = isSameDay(date, today);
  const dCopy = new Date(date); dCopy.setHours(0, 0, 0, 0);
  const tCopy = new Date(today); tCopy.setHours(0, 0, 0, 0);
  if (isToday) return "from-indigo-600 via-purple-600 to-fuchsia-500";
  if (dCopy < tCopy) return "from-slate-500 to-slate-600";
  return "from-emerald-500 to-teal-600";
}

// Phase 45 — payment-type dot helper. Replaces the bulky PaymentBadge
// inside the 7-day clean tile. Five colors: cash, vat, rct, c+i, and
// pending-approval (amber outline).
function paymentDotClass(b) {
  if (b?.payment_status === "pending_manager_approval") return "bg-red-500";
  switch (b?.payment_type_code) {
    case "simple_cash": return "bg-emerald-500";
    case "vat": return "bg-blue-500";
    case "rct": return "bg-zinc-500";
    case "cash_with_invoice": return "bg-amber-500";
    default: return "bg-zinc-300";
  }
}

// Phase B — Configurable booking tile. Renders the 1..5 fields the
// manager picked in Settings → Calendar display, in the chosen order.
// Replaces both CompactBookingTile (desktop weekly grid) and BookingCard
// (mobile day list) on the manager calendar surface. Keeps the chrome
// (left staff stripe, flagged/tag dots, unread chat badge, draggable
// handle) intact so the rest of the calendar still works.
const CALENDAR_FIELD_DEFS = {
  customer_name:     { label: "Customer",        Icon: User },
  company_name:      { label: "Company",         Icon: Building2 },
  job_template:      { label: "Job template",    Icon: Briefcase },
  payment_type:      { label: "Payment type",    Icon: Coins },
  time_start:        { label: "Start time",      Icon: Clock },
  location_postcode: { label: "Postcode",        Icon: MapPin },
  staff_assigned:    { label: "Staff",           Icon: User },
  payment_status:    { label: "Payment status",  Icon: CheckCircle2 },
  notes_truncated:   { label: "Notes",           Icon: MessageSquare },
  booking_code:      { label: "Booking code",    Icon: Hash },
};

function calendarFieldValue(code, b) {
  switch (code) {
    case "customer_name":
      return b.customer_name || "—";
    case "company_name":
      return b.customer_company_name || b.company_name || "—";
    case "job_template":
      return bookingFirstDesc(b);
    case "payment_type":
      return b.payment_type_name || b.payment_type_code || "—";
    case "time_start":
      return formatTime(b.scheduled_start);
    case "location_postcode":
      return b.location?.postcode || b.location?.address_line || "—";
    case "staff_assigned":
      return staffFirstName(b.staff_name) || "—";
    case "payment_status": {
      const s = (b.payment_status || "").replace(/_/g, " ");
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : "—";
    }
    case "notes_truncated": {
      const n = (b.notes || "").trim();
      if (!n) return "—";
      return n.length > 28 ? `${n.slice(0, 28)}…` : n;
    }
    case "booking_code":
      return b.booking_code || "—";
    default:
      return "";
  }
}

function ConfigurableBookingTile({
  b, fields = [], onClick, customerTags = [], unreadCount = 0,
  draggable: isDraggable = false, dragging = false,
  onDragStart, onDragEnd,
  density = "compact",
}) {
  const navigate = useNavigate();
  const { hex } = staffColorVar(b.staff_id);
  const flagged = b.scope_change_flag && !b.scope_change_acknowledged;
  const firstTag = customerTags?.[0];
  const isOneOff = !!b.customer_is_one_off;
  const openProfile = (e) => {
    e.stopPropagation();
    if (b.customer_id) navigate(`/customers/${b.customer_id}`);
  };
  const padding = density === "compact" ? "pl-2 pr-2 py-1.5" : "pl-3 pr-3 py-2";
  const fieldTextSize = density === "compact" ? "text-[11.5px]" : "text-[12.5px]";
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(e); }}
      draggable={isDraggable || undefined}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      data-booking-card="true"
      className={`relative w-full min-w-0 text-left bg-card border border-border hover:border-accent rounded-[6px] ${padding} transition-colors flex flex-col gap-0.5 ${
        isDraggable ? "cursor-grab active:cursor-grabbing" : ""
      } ${dragging ? "opacity-40 ring-2 ring-accent ring-offset-1" : ""}`}
      style={{ borderLeftWidth: 3, borderLeftColor: hex }}
      data-testid={`booking-tile-${b.id}`}
      title={`${formatTime(b.scheduled_start)} · ${b.customer_name || ""}`}
    >
      {/* Phase P.3 Item 1 — persistent staff chip in the top-right corner
          of the desktop weekly tile. Independent of the 5 configurable
          field slots so the manager always has an at-a-glance staff
          signal. Renders first name only (or "N+ staff" if multiple). */}
      {b.staff_name && (
        <span
          className="absolute top-0.5 right-0.5 max-w-[70%] text-[10px] font-semibold text-muted-foreground truncate pointer-events-none"
          data-testid="calendar-booking-staff-chip"
          title={b.staff_name}
        >
          {staffChipLabel(b.staff_name)}
        </span>
      )}
      {b.linked_customer_details_pending && (
        <DetailsPendingDot
          className="absolute top-0.5 left-1"
          testId={`booking-tile-${b.id}-details-pending-dot`}
        />
      )}
      {fields.map((code, i) => {
        const def = CALENDAR_FIELD_DEFS[code];
        if (!def) return null;
        const { Icon, label } = def;
        const value = calendarFieldValue(code, b);
        // Customer name is clickable when shown — links to customer profile.
        const clickable = code === "customer_name" && !!b.customer_id;
        return (
          <div
            key={`${code}-${i}`}
            className={`flex items-center gap-1 min-w-0 ${fieldTextSize}`}
            data-testid={`booking-tile-${b.id}-${code}`}
          >
            <Icon size={11} strokeWidth={2.4} className="shrink-0 text-muted-foreground" aria-hidden="true" />
            {clickable ? (
              <span
                role="link"
                tabIndex={0}
                onClick={openProfile}
                onKeyDown={(e) => { if (e.key === "Enter") openProfile(e); }}
                className="truncate font-semibold hover:underline hover:text-accent"
                title={`Open ${value} profile`}
              >
                {value}
              </span>
            ) : (
              <span
                className={`truncate ${code === "time_start" ? "mono tabular-nums font-bold" : "font-medium"}`}
                title={String(value)}
              >
                {value}
              </span>
            )}
            {code === "customer_name" && isOneOff && (
              <span
                className="shrink-0 mono text-[8px] uppercase tracking-widest font-bold bg-violet-100 text-violet-700 border border-violet-200 rounded-sm px-1 py-[1px]"
                title="Private cash — one-off customer"
              >
                1-off
              </span>
            )}
          </div>
        );
      })}
      {flagged && (
        <span className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-rose-600"
              aria-label="Scope change" />
      )}
      {!flagged && firstTag && (
        <span className="absolute top-0.5 right-0.5"><TagDot tag={firstTag} size={5} /></span>
      )}
      {unreadCount > 0 && (
        <span className="absolute bottom-0.5 right-0.5 min-w-[12px] h-3 px-0.5 rounded-full bg-rose-600 text-[8px] font-bold text-white flex items-center justify-center">
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}
    </button>
  );
}

// Phase 45 — compact 7-day tile. Single visual line with time, customer,
// payment dot, and staff initials chip. Left-edge stripe = staff color.
// Replaces the older BookingCard in the 7-day desktop grid (BookingCard
// is kept and still used by the legacy mobile section list inside the
// 7-day branch; the new tile is mounted only on the desktop column grid).
function CompactBookingTile({ b, onClick, customerTags = [], unreadCount = 0,
                              draggable: isDraggable = false, dragging = false,
                              onDragStart, onDragEnd }) {
  const navigate = useNavigate();
  const { hex } = staffColorVar(b.staff_id);
  const flagged = b.scope_change_flag && !b.scope_change_acknowledged;
  const firstTag = customerTags?.[0];
  // Phase 51.1 — surface manager notes inline on the tile.
  const noteRaw = (b.notes || "").trim();
  const notePreview = noteRaw ? (noteRaw.length > 30 ? `${noteRaw.slice(0, 30)}\u2026` : noteRaw) : "";
  // Phase 59.3 — surface one-off (Private Cash) customers at a glance.
  const isOneOff = !!b.customer_is_one_off;
  // Phase 59.1 — clicking the customer name navigates to the profile,
  // independently of the tile's open-booking onClick.
  const openProfile = (e) => {
    e.stopPropagation();
    if (b.customer_id) navigate(`/customers/${b.customer_id}`);
  };
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(e); }}
      draggable={isDraggable || undefined}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      data-booking-card="true"
      className={`relative w-full min-w-0 text-left bg-card border border-border hover:border-accent rounded-[6px] pl-2 pr-2 py-1.5 transition-colors flex flex-col gap-0.5 group ${
        isDraggable ? "cursor-grab active:cursor-grabbing" : ""
      } ${dragging ? "opacity-40 ring-2 ring-accent ring-offset-1" : ""}`}
      style={{ borderLeftWidth: 3, borderLeftColor: hex }}
      data-testid={`booking-tile-${b.id}`}
      title={`${formatTime(b.scheduled_start)} · ${b.customer_name}${noteRaw ? ` · ${noteRaw}` : ""}${isDraggable ? " · drag to reschedule" : ""}`}
    >
      <div className="flex items-center gap-1.5 min-w-0 w-full">
        <span className={`h-2 w-2 rounded-full shrink-0 ${paymentDotClass(b)}`}
              aria-label={`payment ${b.payment_type_code}`} />
        <span className="mono text-[11px] font-bold tabular-nums tracking-tight shrink-0">
          {formatTime(b.scheduled_start)}
        </span>
        <span
          role="link"
          tabIndex={0}
          onClick={openProfile}
          onKeyDown={(e) => { if (e.key === "Enter") openProfile(e); }}
          className="text-[12px] font-semibold truncate flex-1 hover:underline hover:text-accent transition-colors"
          data-testid={`booking-tile-${b.id}-customer-link`}
          title={`Open ${b.customer_name || "customer"} profile`}
        >
          {b.customer_name || "\u2014"}
        </span>
        {isOneOff && (
          <span
            className="shrink-0 mono text-[8px] uppercase tracking-widest font-bold bg-violet-100 text-violet-700 border border-violet-200 rounded-sm px-1 py-[1px]"
            title="Private cash — one-off customer"
            data-testid={`booking-tile-${b.id}-oneoff`}
          >
            1-off
          </span>
        )}
        <span
          className="shrink-0 mono text-[9px] uppercase tracking-widest font-bold bg-secondary border border-border rounded-sm px-1 py-[1px]"
          style={{ color: hex }}
          data-testid={`booking-tile-${b.id}-initials`}
        >
          {staffInitials(b.staff_name)}
        </span>
      </div>
      {notePreview && (
        <div
          className="text-[11px] text-slate-500 truncate w-full"
          data-testid={`booking-tile-${b.id}-notes`}
        >
          {notePreview}
        </div>
      )}
      {flagged && (
        <span className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-rose-600"
              aria-label="Scope change" />
      )}
      {!flagged && firstTag && (
        <span className="absolute top-0.5 right-0.5"><TagDot tag={firstTag} size={5} /></span>
      )}
      {unreadCount > 0 && (
        <span className="absolute bottom-0.5 right-0.5 min-w-[12px] h-3 px-0.5 rounded-full bg-rose-600 text-[8px] font-bold text-white flex items-center justify-center">
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}
    </button>
  );
}

// Phase 52 — month-view heat-map color scale for the per-day job count
// badge. Used to surface busy vs quiet days at a glance.
// Phase 53 — thresholds are now manager-configurable per tenant. Callers
// pass greenMax / amberMax loaded from /api/settings; the function falls
// back to the historical 3/5 defaults whenever values are missing.
function monthDayCountClass(count, greenMax = 3, amberMax = 5) {
  const g = Number.isFinite(greenMax) && greenMax >= 1 ? greenMax : 3;
  const a = Number.isFinite(amberMax) && amberMax > g ? amberMax : Math.max(g + 1, 5);
  if (count > a) return "bg-red-100 text-red-800";
  if (count > g) return "bg-amber-100 text-amber-800";
  return "bg-emerald-100 text-emerald-800";
}

function ymdKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function bookingFirstDesc(b) {  const lines = b?.line_items || [];
  if (lines.length > 0) return lines[0].description || "—";
  return b?.template_snapshot?.name || "—";
}

function bookingExtraLinesCount(b) {
  const n = (b?.line_items || []).length;
  return n > 1 ? n - 1 : 0;
}

// Phase 30 — inline travel pill rendered between consecutive same-staff
// bookings on the 7-day calendar. The data is already on the second booking
// as `travel_from_previous = {distance_km, duration_min, computed_at}`.
function TravelPill({ travel, prevBooking, nextBooking }) {
  if (!travel) return null;
  const prevAddr = prevBooking?.location?.address_line || prevBooking?.location?.postcode || "previous job";
  const nextAddr = nextBooking?.location?.address_line || nextBooking?.location?.postcode || "this job";
  const minutes = Math.round(Number(travel.duration_min || 0));
  const km = Number(travel.distance_km || 0);
  return (
    <div
      className="flex items-center gap-1.5 -my-0.5 select-none"
      title={`${prevAddr} → ${nextAddr}`}
      data-testid={nextBooking ? `travel-pill-${nextBooking.id}` : undefined}
    >
      <div className="h-3 w-px bg-sky-300 ml-3" aria-hidden="true" />
      <span className="inline-flex items-center gap-1 text-[10px] mono uppercase tracking-wider text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5">
        <span aria-hidden="true">↗</span>
        {minutes} min · {km} km
      </span>
    </div>
  );
}


// Phase 36 — calendar tile for a tentative enquiry. Dashed amber border,
// PENDING pill, click → opens the enquiry's edit panel (NOT BookingDetail).
function EnquiryTile({ enquiry, onClick }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
      data-booking-card="true"
      data-enquiry-tile="true"
      className="relative w-full text-left bg-amber-50/70 border-2 border-amber-400 border-dashed rounded-[6px] pl-3 pr-3 py-2 hover:border-amber-600 hover:bg-amber-50 transition-colors"
      data-testid={`enquiry-tile-${enquiry.id}`}
    >
      <div className="flex items-center justify-between gap-2 min-w-0">
        <span className="mono font-bold text-sm tabular-nums text-amber-900">
          {enquiry.tentative_start ? formatTime(enquiry.tentative_start) : "\u2014:\u2014"}
        </span>
        <span className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest font-bold text-amber-800 bg-amber-200/70 border border-amber-300 rounded-sm px-1.5 py-0.5 shrink-0"
              data-testid={`enquiry-tile-pending-pill-${enquiry.id}`}>
          Pending
        </span>
      </div>
      <div className="text-[13px] font-semibold truncate mt-0.5 text-amber-950">
        {enquiry.customer_name || "\u2014"}
      </div>
      {enquiry.notes && (
        <div className="text-[11px] text-amber-900/80 line-clamp-1 mt-0.5">
          {enquiry.notes}
        </div>
      )}
    </button>
  );
}


function BookingCard({ b, onClick, draggable = false, onDragStart, onDragEnd, dragging = false, customerTags = [], unreadCount = 0 }) {
  const { cls, hex } = staffColorVar(b.staff_id);
  const flagged = b.scope_change_flag && !b.scope_change_acknowledged;
  const firstTag = customerTags?.[0];
  // Phase 51.1 — surface manager notes inline on mobile / day-detail card.
  const noteRaw = (b.notes || "").trim();
  const notePreview = noteRaw ? (noteRaw.length > 30 ? `${noteRaw.slice(0, 30)}\u2026` : noteRaw) : "";
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick && onClick(e); }}
      draggable={draggable || undefined}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      data-booking-card="true"
      className={`relative w-full min-w-0 overflow-hidden text-left bg-card border border-border hover:border-accent rounded-[6px] pl-3 pr-3 py-2 transition-colors ${dragging ? "opacity-40" : ""}`}
      style={{ borderLeftWidth: 3, borderLeftColor: hex }}
      data-testid={`booking-card-${b.id}`}
    >
      {flagged && (
        <span
          className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-rose-600 ring-2 ring-card"
          data-testid={`booking-card-scope-dot-${b.id}`}
          aria-label="Scope change flagged"
        />
      )}
      {!flagged && firstTag && (
        <span
          className="absolute top-1.5 right-1.5"
          title={customerTags.map((t) => t.name).join(", ")}
          data-testid={`booking-card-tag-dot-${b.id}`}
        >
          <TagDot tag={firstTag} size={6} />
        </span>
      )}
      {/* Phase 20 — unread chat from staff. Stack slightly below the scope/tag dot
          so both signals can coexist without overlap. */}
      {unreadCount > 0 && (
        <span
          className="absolute top-5 right-1.5 min-w-[14px] h-[14px] px-1 rounded-full bg-rose-600 text-[9px] font-bold text-white flex items-center justify-center ring-2 ring-card"
          data-testid={`booking-chat-unread-badge-${b.id}`}
          aria-label={`${unreadCount} unread chat message${unreadCount > 1 ? "s" : ""}`}
        >
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}
      <div className="flex items-center justify-between gap-2 min-w-0">
        <span className="mono font-bold text-sm tabular-nums">{formatTime(b.scheduled_start)}</span>
        <span className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">
          <span className={`h-1.5 w-1.5 rounded-full ${cls}`} />
          {staffFirstName(b.staff_name)}
        </span>
      </div>
      <div className="text-[13px] font-semibold truncate mt-0.5 flex items-center gap-1">
        <span className="truncate">{bookingFirstDesc(b)}</span>
        {bookingExtraLinesCount(b) > 0 && (
          <span className="shrink-0 mono text-[10px] uppercase tracking-widest text-accent border border-accent/40 rounded px-1"
                data-testid={`booking-card-multi-${b.id}`}>+{bookingExtraLinesCount(b)}</span>
        )}
      </div>
      {notePreview && (
        <div
          className="text-[11px] text-slate-500 truncate mt-0.5"
          data-testid={`booking-card-${b.id}-notes`}
          title={noteRaw}
        >
          {notePreview}
        </div>
      )}
      {/* Phase 22 — always-visible payment badge. Vivid amber for cash variants so
          the office team can spot collect-on-site jobs at a glance. Sits on its
          own row so it never gets truncated by long customer/service text.
          Phase 39 — use the `compact` size in the 7-day grid: the full
          "CASH — COLLECT" label was overflowing into the next column at
          1024-1280px viewports. The shortText ("CASH", "C+I", "VAT", "RCT")
          stays inside the tile and the full label remains in the tooltip. */}
      <div className="mt-1 flex min-w-0">
        <PaymentBadge job={b} size="compact" />
      </div>
    </button>
  );
}

function BookingDetail({ id, onClose, onChanged, currency, paymentTypes, customerTagsByCustomer, requireLocation = true }) {
  const [b, setB] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);
  // Phase 32 — latest payment-approval row attached to this booking
  // (pending OR most-recent decision). Surfaced inline as an
  // Approve/Reject panel when decision==null.
  const [approval, setApproval] = useState(null);
  const [approving, setApproving] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectBusy, setRejectBusy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/bookings/${id}`);
      setB(data);
      // Look up invoice if any
      try {
        const { data: invs } = await api.get("/invoices", { params: { include_excluded_types: true } });
        const match = invs.find((x) => x.booking_id === data.id);
        setInvoice(match || null);
      } catch { setInvoice(null); }
      // Phase 32 — surface latest payment-approval for this booking.
      try {
        const { data: appr } = await api.get(`/payment-approvals/by-booking/${id}`);
        setApproval(appr || null);
      } catch { setApproval(null); }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  }, [id]);

  useEffect(() => { if (id) load(); }, [id, load]);

  const approvePayment = async () => {
    if (!approval) return;
    setApproving(true);
    try {
      await api.post(`/payment-approvals/${approval.id}/approve`);
      toast.success("Approved — payment marked paid");
      onChanged?.();
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not approve");
    } finally {
      setApproving(false);
    }
  };
  const rejectPayment = async () => {
    if (!approval) return;
    if (!rejectReason.trim()) { toast.error("Reason is required"); return; }
    setRejectBusy(true);
    try {
      await api.post(`/payment-approvals/${approval.id}/reject`, { rejection_reason: rejectReason.trim() });
      toast.success("Rejected — staff will be notified");
      setRejectReason("");
      setRejectOpen(false);
      onChanged?.();
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not reject");
    } finally {
      setRejectBusy(false);
    }
  };

  const onDelete = async () => {
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Booking deleted");
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  if (!b) return null;
  const loc = b.location || {};
  // Phase 50.3 — centralised, location_url-aware helper.
  const mapsHref = buildMapsUrl(loc);

  return (
    <div className="space-y-4" data-testid="booking-detail">
      <div className="space-y-1">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// {b.booking_code}</div>
          {/* Phase 22 — large payment badge at top-right of the detail panel. */}
          <PaymentBadge job={b} size="lg" />
        </div>
        <h3 className="text-2xl font-extrabold leading-tight">
          {b.customer_id ? (
            <Link
              to={`/customers/${b.customer_id}`}
              className="hover:underline hover:text-accent transition-colors"
              data-testid="booking-detail-customer-link"
            >
              {b.customer_name}
            </Link>
          ) : (
            b.customer_name
          )}
        </h3>
        {b.customer_contact && <div className="text-sm text-muted-foreground">{b.customer_contact}</div>}
        {b.created_at && (
          <div
            className="text-xs text-muted-foreground mono tabular-nums"
            data-testid="booking-detail-created-at"
            title={new Date(b.created_at).toLocaleString()}
          >
            Booked {formatBookingStamp(b.created_at)}
            {b.completed_at && (
              <> · Completed {formatBookingStamp(b.completed_at)}
                {b.completed_by_name && ` by ${b.completed_by_name}`}
              </>
            )}
          </div>
        )}
        {(customerTagsByCustomer?.[b.customer_id] || []).length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1" data-testid="booking-customer-tags">
            {customerTagsByCustomer[b.customer_id].map((t) => (
              <TagChip key={t.id} tag={t} size="xs" />
            ))}
          </div>
        )}
      </div>

      {b.scope_change_flag && !b.scope_change_acknowledged && (
        <div className="bg-rose-50 border border-rose-200 rounded-[6px] p-3 text-sm text-rose-900" data-testid="scope-flag-banner">
          <div className="font-bold flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Scope change flagged</div>
          <div className="mt-1 text-rose-800">{b.scope_change_note}</div>
          <Button
            size="sm"
            variant="outline"
            className="mt-2"
            data-testid="acknowledge-scope-button"
            onClick={async () => {
              try {
                await api.post(`/alerts/${b.id}/acknowledge`);
                toast.success("Acknowledged");
                onChanged?.();
                load();
              } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
            }}
          >
            Acknowledge
          </Button>
        </div>
      )}

      {/* Phase 32 — Inline payment-approval panel. Surfaces the latest
          approval row tied to this booking. Pending shows Approve/Reject
          CTAs and the staff-supplied method/amount/note; decided rows
          show the audit timeline. */}
      {approval && (
        <div
          className={`rounded-[8px] border p-3 space-y-2 text-sm ${
            approval.decision == null
              ? "bg-amber-50 border-amber-300"
              : approval.decision === "approved"
              ? "bg-emerald-50 border-emerald-300"
              : "bg-rose-50 border-rose-300"
          }`}
          data-testid="booking-approval-panel"
          data-decision={approval.decision || "pending"}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] mono uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${
              approval.decision == null
                ? "bg-amber-200 text-amber-900"
                : approval.decision === "approved"
                ? "bg-emerald-200 text-emerald-900"
                : "bg-rose-200 text-rose-900"
            }`}>
              {approval.decision == null ? "PENDING APPROVAL" : approval.decision.toUpperCase()}
            </span>
            <span className="text-xs text-muted-foreground">
              marked by <span className="font-semibold text-foreground">{approval.marked_by_staff_name || "—"}</span>
              {" · "}
              method <span className="mono uppercase">{approval.method}</span>
              {approval.amount != null && (
                <> {" · "} amount <span className="mono font-bold text-foreground">{Number(approval.amount).toFixed(2)} {currency || "EUR"}</span></>
              )}
            </span>
          </div>
          {approval.note && (
            <div className="text-xs italic text-muted-foreground">"{approval.note}"</div>
          )}
          {approval.rejection_reason && (
            <div className="text-xs text-rose-900">Reason: {approval.rejection_reason}</div>
          )}
          {approval.decision == null && (
            <div className="flex items-center gap-2 pt-1">
              <Button
                size="sm"
                onClick={approvePayment}
                disabled={approving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 gap-1"
                data-testid="booking-approval-approve"
              >
                {approving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Approve"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRejectOpen(true)}
                className="border-rose-300 text-rose-700 hover:bg-rose-50 h-8"
                data-testid="booking-approval-reject"
              >
                Reject
              </Button>
            </div>
          )}
          {/* Audit timeline — last 3 entries, newest first. */}
          {Array.isArray(approval.audit_log) && approval.audit_log.length > 0 && (
            <ul className="mt-1 text-[11px] text-muted-foreground space-y-0.5" data-testid="booking-approval-audit">
              {[...approval.audit_log].slice(-3).reverse().map((entry, i) => (
                <li key={i}>
                  <span className="mono uppercase">{entry.action}</span>
                  {" · "}{entry.actor_type}{" · "}
                  {new Date(entry.at).toLocaleString()}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="booking-approval-reject-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reject payment mark</DialogTitle>
            <DialogDescription>
              Let the staff know what's missing — they'll see this on their job card.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. amount mismatch — please recount and resubmit"
            data-testid="booking-approval-reject-reason"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)} disabled={rejectBusy}>Cancel</Button>
            <Button
              onClick={rejectPayment}
              disabled={rejectBusy}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="booking-approval-reject-confirm"
            >
              {rejectBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Row label="Time">
        <span className="font-bold mono">{new Date(b.scheduled_start).toLocaleString()} · {b.scheduled_duration_minutes}m</span>
      </Row>
      <Row label="Staff"><User className="h-3.5 w-3.5 inline mr-1" />{b.staff_name}</Row>
      <Row label="Services">
        <div className="space-y-1.5 w-full" data-testid="booking-line-items">
          {(b.line_items || []).map((li, i) => (
            <div key={li.id || i} className="flex items-center gap-2 text-sm" data-testid={`booking-line-item-${i}`}>
              <Tag className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 truncate">{li.description}{li.quantity > 1 ? ` ×${li.quantity}` : ""}</span>
              <span className="mono text-xs font-bold shrink-0">{formatCurrency(li.line_total, currency)}</span>
            </div>
          ))}
          {b.subtotal_before_discount > 0 && (
            <div className="border-t border-border mt-2 pt-2 space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="mono font-bold">{formatCurrency(b.subtotal_before_discount, currency)}</span>
              </div>
              {b.discount_amount > 0 && (
                <div className="flex justify-between text-rose-700">
                  <span>Discount{b.discount_type === "percent" ? ` (${b.discount_value}%)` : ""}</span>
                  <span className="mono font-bold">−{formatCurrency(b.discount_amount, currency)}</span>
                </div>
              )}
              {b.discount_amount > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">After discount</span>
                  <span className="mono font-bold">{formatCurrency(b.subtotal, currency)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </Row>
      <Row label="Location" hidden={!requireLocation}>
        <div className="space-y-0.5" data-testid="booking-detail-location-row">
          {loc.address_line && (
            <div
              className="whitespace-pre-wrap select-text cursor-text"
              onClick={(e) => e.stopPropagation()}
            >{linkifyText(loc.address_line)}</div>
          )}
          {(loc.postcode || loc.eircode) && <div className="mono text-xs">{loc.postcode || loc.eircode}</div>}
          {mapsHref && (
            <a href={mapsHref} target="_blank" rel="noreferrer" className="text-accent text-xs inline-flex items-center gap-1 hover:underline">
              <MapPin className="h-3 w-3" /> Open in Maps <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </Row>
      <Row label="Payment"><PaymentChip type={b.payment_type} status={b.payment_status} /></Row>
      {b.customer_phone && (
        <Row label="Customer phone">
          <a href={`tel:${b.customer_phone}`} className="text-accent inline-flex items-center gap-1 hover:underline">
            <Phone className="h-3 w-3" /> {b.customer_phone}
          </a>
        </Row>
      )}
      {requireLocation && b.travel_from_previous && (
        <Row label="Travel">
          <div data-testid="booking-detail-travel">
            <div className="flex items-center gap-2">
              <span className="text-[10px] mono uppercase tracking-wider text-muted-foreground">From previous</span>
              <span className="font-bold text-sky-700">
                ↗ {Math.round(Number(b.travel_from_previous.duration_min || 0))} min · {b.travel_from_previous.distance_km} km
              </span>
            </div>
            {b.travel_from_previous.computed_at && (
              <div className="mono text-[10px] text-muted-foreground mt-0.5">
                Computed {new Date(b.travel_from_previous.computed_at).toLocaleString()}
              </div>
            )}
          </div>
        </Row>
      )}
      {b.notes && <Row label="Manager notes"><div className="whitespace-pre-wrap text-sm select-text cursor-text" data-testid={`booking-detail-${b.id}-notes`}>{linkifyText(b.notes)}</div></Row>}
      {b.staff_notes && <Row label="Staff notes"><div className="whitespace-pre-wrap text-sm select-text cursor-text" data-testid={`booking-detail-${b.id}-staff-notes`}>{linkifyText(b.staff_notes)}</div></Row>}

      {invoice && (
        <div className="bg-card border border-accent rounded-[8px] p-3 mt-2" data-testid={`booking-invoice-card-${invoice.id}`}>
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-accent">// Invoice</div>
              <div className="font-extrabold mono mt-0.5">{invoice.invoice_number}</div>
              <div className="text-xs text-muted-foreground">{invoice.payment_type_name} · <span className="font-bold">{formatCurrency(invoice.total, invoice.currency)}</span></div>
            </div>
            <Button size="sm" className="btn-cta" onClick={() => navigate(`/invoices/${invoice.id}`)} data-testid="open-invoice-button">
              View
            </Button>
          </div>
        </div>
      )}
      {b.staff_photos?.length > 0 && (
        <Row label={`Staff photos (${b.staff_photos.length})`}>
          <div className="grid grid-cols-3 gap-2">
            {b.staff_photos.map((p) => (
              <a key={p.filename} href={p.url} target="_blank" rel="noreferrer">
                <img src={p.url} alt="" className="rounded-[6px] border border-border w-full h-20 object-cover" />
              </a>
            ))}
          </div>
        </Row>
      )}

      <div className="flex flex-wrap gap-2 pt-3 border-t border-border" data-testid="booking-status-actions">
        <BookingStatusActions
          booking={b}
          paymentTypes={paymentTypes}
          busy={statusBusy}
          onChange={async (newStatus) => {
            setStatusBusy(true);
            try {
              await api.patch(`/bookings/${b.id}/status`, { status: newStatus });
              const ptCode = b.payment_type;
              const pt = (paymentTypes || []).find((p) => p.code === ptCode);
              if (newStatus === "completed" && pt?.generates_invoice) {
                toast.success(`Marked complete · invoice will be auto-issued`);
              } else if (newStatus === "completed") {
                toast.success("Marked complete");
              } else if (newStatus === "in_progress") {
                toast.success("Job started");
              } else if (newStatus === "scheduled") {
                toast.success("Reopened to scheduled");
              } else if (newStatus === "cancelled") {
                toast.success("Booking cancelled");
              }
              await load();
              onChanged?.();
            } catch (e) {
              toast.error(formatApiError(e?.response?.data?.detail));
            } finally {
              setStatusBusy(false);
            }
          }}
        />
      </div>

      <div className="flex flex-wrap gap-2 pt-3 border-t border-border">
        <Button onClick={() => navigate(`/bookings/${b.id}/edit`)} data-testid="booking-edit-button" className="btn-cta">
          <Pencil className="h-4 w-4 mr-1" /> Edit
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" data-testid="booking-delete-button">
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {b.booking_code}?</AlertDialogTitle>
              <AlertDialogDescription>This permanently removes the booking.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={onDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="booking-delete-confirm">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <BookingConfirmationButton booking={b} onSent={(updated) => onChanged && onChanged(updated)} />

      {/* Phase 20 — Chat with assigned staff. Hidden when no staff assigned. */}
      {b.staff_id && (
        <BookingChat
          bookingId={b.id}
          side="manager"
          label={`Chat with ${b.staff_name || "staff"}`}
          emptyHint={`No messages yet. Send ${b.staff_name?.split(" ")[0] || "your engineer"} a note.`}
        />
      )}

      {/* Phase P.11 — Status / payment history timeline. Collapsed by default. */}
      <StatusHistory entityType="booking" entityId={b.id} testIdPrefix="booking-history" />
    </div>
  );
}

function BookingConfirmationButton({ booking, onSent }) {
  const [open, setOpen] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [checks, setChecks] = useState({});
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/customers/${booking.customer_id}`).then(({ data }) => {
      setCustomer(data);
      const ch = {};
      (data.emails || []).forEach((e) => { if (e.email) ch[e.email] = !!e.receives_invoices; });
      setChecks(ch);
    }).catch(() => {});
  }, [open, booking.customer_id]);

  const send = async () => {
    const recipients = [
      ...Object.keys(checks).filter((k) => checks[k]),
      ...extra.split(",").map((s) => s.trim()).filter((s) => s.includes("@")),
    ];
    if (!recipients.length) return toast.error("Pick at least one recipient");
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/send-confirmation`, { recipient_emails: recipients, channel: "email" });
      toast.success("Confirmation sent");
      // Phase P.4 Item 2 — endpoint now returns {channel, sent_to, booking};
      // pass the fresh booking back to the parent for local state refresh.
      onSent?.(data?.booking || data);
      setOpen(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "no_customer_email") {
        toast.error(detail.message || "Customer has no email — try WhatsApp instead");
      } else {
        toast.error(formatApiError(detail));
      }
    }
    finally { setBusy(false); }
  };

  // Phase P.4 Item 2 — WhatsApp channel. Server returns the deep-link URL;
  // we open it in a new tab and the manager sends the message themselves.
  // Works on Basic tier (email-sending gate doesn't apply).
  const sendWhatsapp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/send-confirmation`, { channel: "whatsapp" });
      if (data?.wa_url) {
        window.open(data.wa_url, "_blank", "noopener,noreferrer");
        toast.success("Opening WhatsApp — send from there");
      }
      setOpen(false);
      // Trigger a booking refresh so the "last sent" line updates.
      onSent?.(booking);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "no_customer_phone") {
        toast.error(detail.message || "Customer has no phone number — add one first");
      } else {
        toast.error(formatApiError(detail));
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="pt-3 border-t border-border" data-testid="booking-confirmation-row">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" data-testid="booking-send-confirmation-btn">
            <Mail className="h-3.5 w-3.5 mr-1" /> Send confirmation
          </Button>
        </DialogTrigger>
        <DialogContent data-testid="booking-confirmation-modal">
          <DialogHeader>
            <DialogTitle>Send booking confirmation</DialogTitle>
            <DialogDescription>{booking.booking_code} · {booking.customer_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(customer?.emails || []).filter((e) => e.email).map((e) => (
              <label key={e.email} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!checks[e.email]} onChange={(ev) => setChecks((c) => ({ ...c, [e.email]: ev.target.checked }))} data-testid={`booking-confirmation-email-${e.email}`} />
                <span>{e.email}</span>
              </label>
            ))}
            {(!customer?.emails || customer.emails.length === 0) && (
              <div className="text-sm text-muted-foreground">No emails on file for this customer.</div>
            )}
            <div className="pt-2">
              <Label className="text-xs">Other (comma-separated)</Label>
              <Input value={extra} onChange={(ev) => setExtra(ev.target.value)} placeholder="extra@example.com" data-testid="booking-confirmation-extra-input" />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:justify-between flex-wrap">
            <Button
              variant="outline"
              onClick={sendWhatsapp}
              disabled={busy}
              className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
              data-testid="send-confirmation-whatsapp"
              title="Opens WhatsApp with a pre-filled confirmation message"
            >
              📱 WhatsApp
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                onClick={send}
                disabled={busy}
                className="btn-cta"
                data-testid="send-confirmation-email"
              >
                {busy ? "Sending..." : "📧 Email"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {booking.confirmation_sent_at && (
        <span
          className="ml-3 text-[11px] text-emerald-700"
          data-testid="booking-confirmation-history"
        >
          <Send className="h-3 w-3 inline mr-1" />
          Last sent via {booking.confirmation_channel || "email"} on {(booking.confirmation_sent_at || "").slice(0, 16).replace("T", " ")}
        </span>
      )}
    </div>
  );
}


function BookingStatusActions({ booking, paymentTypes, busy, onChange }) {
  const status = booking.status;
  const ptCode = booking.payment_type;
  const pt = (paymentTypes || []).find((p) => p.code === ptCode);
  const willInvoice = !!pt?.generates_invoice;

  const Pill = ({ tone }) => {
    const cls = {
      scheduled: "bg-amber-100 text-amber-900 border-amber-200",
      in_progress: "bg-sky-100 text-sky-900 border-sky-200",
      completed: "bg-emerald-100 text-emerald-900 border-emerald-200",
      cancelled: "bg-zinc-200 text-zinc-700 border-zinc-300",
    }[tone] || "bg-zinc-100";
    const label = {
      scheduled: "Scheduled",
      in_progress: "In progress",
      completed: "Completed",
      cancelled: "Cancelled",
    }[tone] || tone;
    return (
      <span className={`mono text-[10px] uppercase tracking-widest border px-2 py-1 rounded ${cls}`} data-testid="booking-status-pill">
        {label}
      </span>
    );
  };

  return (
    <>
      <Pill tone={status} />
      {status === "scheduled" && (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onChange("in_progress")} data-testid="status-start-job">
          Start job
        </Button>
      )}
      {status === "in_progress" && (
        <Button size="sm" className="btn-cta" disabled={busy} onClick={() => onChange("completed")} data-testid="status-mark-complete">
          Mark complete{willInvoice ? " · auto-invoice" : ""}
        </Button>
      )}
      {status === "scheduled" && (
        <Button size="sm" className="btn-cta" disabled={busy} onClick={() => onChange("completed")} data-testid="status-mark-complete-direct">
          Mark complete{willInvoice ? " · auto-invoice" : ""}
        </Button>
      )}
      {status === "completed" && (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onChange("in_progress")} data-testid="status-reopen">
          Reopen
        </Button>
      )}
      {status === "cancelled" && (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onChange("scheduled")} data-testid="status-reactivate">
          Reactivate
        </Button>
      )}
    </>
  );
}

function Row({ label, children, hidden = false }) {
  if (hidden) return null;
  return (
    <div className="grid grid-cols-[120px_1fr] gap-3 items-start">
      <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground pt-0.5">{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

export default function Calendar() {
  const navigate = useNavigate();
  // Phase 7: view = "week" | "month", persisted to localStorage.
  // Phase 63.2: bottom nav deep-links via `?view=week|month` — that wins over
  // the localStorage default on the very first paint and on subsequent
  // back/forward/url-paste navigations.
  // Phase P.12: added `day` as a third view mode. Storage key migrated to
  // `jobsbook:calendar-view`; the legacy `jbk_calendar_view` key still gets
  // read once on first paint for a graceful upgrade.
  const _initialView = (() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const wanted = sp.get("view");
      if (wanted === "day" || wanted === "week" || wanted === "month") return wanted;
      const primary = localStorage.getItem("jobsbook:calendar-view");
      if (primary === "day" || primary === "week" || primary === "month") return primary;
      const legacy = localStorage.getItem("jbk_calendar_view");
      if (legacy === "month") return "month";
      return "week";
    } catch {
      return "week";
    }
  })();
  const [view, setView] = useState(_initialView);
  useEffect(() => {
    try { localStorage.setItem("jobsbook:calendar-view", view); } catch { /* noop */ }
  }, [view]);

  const [windowStart, setWindowStart] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [monthAnchor, setMonthAnchor] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [monthSelectedDay, setMonthSelectedDay] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  });
  // Phase P.12 — Day view anchor. Independent from monthSelectedDay so
  // switching between Month and Day preserves the last day you were on
  // when you flip back and forth.
  const [dayAnchor, setDayAnchor] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  });
  const [bookings, setBookings] = useState([]);
  // Phase P.27 — calendar day-blocks + notes. Kept as flat arrays fetched
  // alongside bookings on every window change (small payload, cheap).
  const [calendarBlocks, setCalendarBlocks] = useState([]);
  const [calendarNotes, setCalendarNotes] = useState([]);
  // Phase P.27 — inline modals for the block/note flows. All keep the parent
  // Calendar responsible for the fetch cycle so a mutation flows back through
  // the same `load()` used by every other write path.
  const [blockDayModal, setBlockDayModal] = useState(null); // {date: Date, existing?: block}
  const [notePickerModal, setNotePickerModal] = useState(null); // {at: Date, editing?: note}
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  // Phase K — staff filter chip + show-free toggle on the calendar header.
  // Persisted to URL `?staff=<id|all>` so reload / share keeps the view.
  const [staffList, setStaffList] = useState([]);
  const [staffShowFreeOnly, setStaffShowFreeOnly] = useState(false);
  // Phase 23 — open a booking directly via `?bookingId=<id>` URL param.
  // Lets the Jobs page (and any other surface) deep-link into the calendar's
  // existing BookingDetail sheet without us re-implementing the panel.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const bid = searchParams.get("bookingId");
    if (bid && bid !== openId) {
      setOpenId(bid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  // Phase 63.2 — keep the local `view` in sync when the bottom nav (or any
  // other deep-link) changes `?view=week|month` after mount. Without this,
  // the second tap on a Calendar bottom-nav button wouldn't flip views.
  useEffect(() => {
    const wanted = searchParams.get("view");
    if ((wanted === "day" || wanted === "week" || wanted === "month") && wanted !== view) {
      setView(wanted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [currency, setCurrency] = useState("EUR");
  const [requireLocation, setRequireLocation] = useState(true);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [customerTagsByCustomer, setCustomerTagsByCustomer] = useState({});
  // Phase 20 — booking_id → unread chat count from staff. Polled every 30s.
  const [unreadByBooking, setUnreadByBooking] = useState({});
  // Phase 4 — drag-to-reschedule state (week view only)
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [pendingMove, setPendingMove] = useState(null); // { booking, newDate }
  const [moveBusy, setMoveBusy] = useState(false);
  // Phase 48 — soft conflict warning for drag-drop reschedule. Sequential:
  // we show this BEFORE the RescheduleDialog and never stack the two.
  const [warnOnTimeConflicts, setWarnOnTimeConflicts] = useState(true);
  // Phase 53 — manager-configurable thresholds for the month-view
  // job-count heat-map. Defaults to the historical 3/5 split.
  const [heatmapGreenMax, setHeatmapGreenMax] = useState(3);
  const [heatmapAmberMax, setHeatmapAmberMax] = useState(5);
  // Phase F — "My cash only" filter. Visible if and only if the current
  // manager has at least one Paid Method linked to their user id. Toggle
  // state persists in localStorage per user.
  const { manager } = useAuth();
  const [hasMyCashLink, setHasMyCashLink] = useState(false);
  const [myCashOnly, setMyCashOnly] = useState(() => {
    try { return localStorage.getItem("jb_calendar_my_cash_only") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("jb_calendar_my_cash_only", myCashOnly ? "1" : "0"); }
    catch { /* noop */ }
  }, [myCashOnly]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings/paid-methods");
        if (cancelled) return;
        const mine = Array.isArray(data) ? data.some((p) => p.user_id && p.user_id === manager?.id) : false;
        setHasMyCashLink(mine);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [manager?.id]);
  // Phase B — manager-configurable booking card display fields.
  // Default mirrors the historical layout (customer + job + time + payment).
  const [calendarFields, setCalendarFields] = useState([
    "customer_name", "job_template", "time_start", "payment_type", "payment_status",
  ]);
  const [moveConflicts, setMoveConflicts] = useState([]);
  const [moveConflictPending, setMoveConflictPending] = useState(null); // { booking, newDate }
  // Phase 7 — quick booking-create from month-cell empty click
  const [quickCreateDate, setQuickCreateDate] = useState(null);
  // Phase 36 — tentative enquiries
  const [enquiries, setEnquiries] = useState([]);
  const [enquiriesLoading, setEnquiriesLoading] = useState(true);
  const [enquiryDialogOpen, setEnquiryDialogOpen] = useState(false);
  const [editingEnquiry, setEditingEnquiry] = useState(null);
  // Phase 41 — Quick Booking sheet/dialog.
  const [quickBookingOpen, setQuickBookingOpen] = useState(false);
  // Phase 45 — DayDetailDrawer state. Replaces the legacy
  // "tap-day-jumps-straight-to-create-form" flow. `drawerDate` is the
  // anchor for the drawer; `quickBookingInitialDate` /
  // `enquiryInitialDate` carry the prefill date when one of the
  // in-drawer "Add" buttons fires.
  const [drawerDate, setDrawerDate] = useState(null);
  const [quickBookingInitialDate, setQuickBookingInitialDate] = useState(null);
  const [enquiryInitialDate, setEnquiryInitialDate] = useState(null);
  const openDrawerForDate = (d) => {
    const normalized = new Date(d);
    normalized.setHours(0, 0, 0, 0);
    setDrawerDate(normalized);
  };

  const days = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(windowStart);
      d.setDate(windowStart.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [windowStart]);

  // Phase 7: 6x7 grid for the month view, ISO Monday-first.
  const monthGridDays = useMemo(() => {
    const { year, month } = monthAnchor;
    const first = new Date(year, month, 1); first.setHours(0, 0, 0, 0);
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() - ((first.getDay() + 6) % 7));
    const arr = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      d.setHours(0, 0, 0, 0);
      arr.push(d);
    }
    return arr;
  }, [monthAnchor]);

  const monthLabel = useMemo(() => {
    const { year, month } = monthAnchor;
    return new Date(year, month, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  }, [monthAnchor]);

  // Phase K — staff filter (from URL `?staff=<id|all>`). The chip uses
  // `setSearchParams` to keep it shareable; clearing returns to "all".
  const staffFilterId = (searchParams.get("staff") || "all").toLowerCase();
  const setStaffFilter = useCallback((next) => {
    const params = new URLSearchParams(searchParams);
    if (!next || next === "all") params.delete("staff");
    else params.set("staff", next);
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  // Phase K — bookings rendered on the calendar, filtered by the chip.
  // Drag/edit lookups still use the raw `bookings` list so cross-staff
  // operations remain possible (e.g. manager moves any booking around).
  const filteredBookings = useMemo(() => {
    if (!staffFilterId || staffFilterId === "all") return bookings;
    return bookings.filter((b) => b.staff_id === staffFilterId);
  }, [bookings, staffFilterId]);

  // Phase K — per-staff booking counts for the chip popover badges.
  const staffBookingCounts = useMemo(() => {
    const out = {};
    bookings.forEach((b) => {
      if (b.staff_id) out[b.staff_id] = (out[b.staff_id] || 0) + 1;
    });
    return out;
  }, [bookings]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let from, to;
      let compactFetch = false;
      if (view === "month") {
        from = new Date(monthGridDays[0]).toISOString();
        const end = new Date(monthGridDays[monthGridDays.length - 1]);
        end.setDate(end.getDate() + 1);
        to = end.toISOString();
        // Phase P.12 — month view uses the compact shape for perf. The
        // BookingDetail sheet still fetches the full doc via
        // GET /api/bookings/{id} on click.
        compactFetch = true;
      } else if (view === "day") {
        // Phase P.12 — day view: fetch a 24h window plus a small buffer
        // on either side so time-widening works cleanly at midnight.
        const start = new Date(dayAnchor); start.setHours(0, 0, 0, 0);
        const end = new Date(dayAnchor); end.setDate(end.getDate() + 1); end.setHours(0, 0, 0, 0);
        from = start.toISOString();
        to = end.toISOString();
      } else {
        from = new Date(windowStart).toISOString();
        const end = new Date(windowStart);
        end.setDate(end.getDate() + 7);
        to = end.toISOString();
      }
      const bookingsParams = { from, to };
      if (compactFetch) bookingsParams.fields = "compact";
      // Phase P.27 — fetch calendar blocks + notes for the same window.
      // Cheap side-fetch (few docs typically); wire into the same Promise.all
      // so a slow endpoint can't stagger the calendar render.
      const rangeParams = { from_date: from.slice(0, 10), to_date: to.slice(0, 10) };
      const [bRes, sRes, custRes, stRes, blocksRes, notesRes] = await Promise.all([
        api.get("/bookings", { params: bookingsParams }),
        api.get("/settings").catch(() => ({ data: { currency: "EUR" } })),
        api.get("/customers").catch(() => ({ data: [] })),
        api.get("/staff").catch(() => ({ data: [] })),
        api.get("/calendar-blocks", { params: rangeParams }).catch(() => ({ data: [] })),
        api.get("/calendar-notes", { params: rangeParams }).catch(() => ({ data: [] })),
      ]);
      // Phase 9.1: in audit mode, hide non-accounts payment types from manager dropdowns.
      const ptRes = await api.get("/settings/payment-types", {
        params: sRes.data?.audit_mode ? { hide_non_accounts: true } : {},
      }).catch(() => ({ data: [] }));
      setBookings(bRes.data);
      // Phase P.27 — cached parallel with bookings.
      setCalendarBlocks(Array.isArray(blocksRes.data) ? blocksRes.data : []);
      setCalendarNotes(Array.isArray(notesRes.data) ? notesRes.data : []);
      // Phase K — keep active staff sorted alphabetically for the filter chip.
      setStaffList(
        (Array.isArray(stRes.data) ? stRes.data : [])
          .filter((s) => s && s.active !== false)
          .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
      );
      setCurrency(sRes.data?.currency || "EUR");
      setRequireLocation(sRes.data?.require_location !== false);
      // Phase 48 — settings toggle for soft conflict warning.
      setWarnOnTimeConflicts(sRes.data?.warn_on_time_conflicts !== false);
      // Phase 53 — month-view heat-map thresholds.
      const gMax = Number(sRes.data?.month_heatmap_green_max);
      const aMax = Number(sRes.data?.month_heatmap_amber_max);
      setHeatmapGreenMax(Number.isFinite(gMax) && gMax >= 1 ? gMax : 3);
      setHeatmapAmberMax(Number.isFinite(aMax) && aMax >= 2 ? aMax : 5);
      // Phase B — calendar booking-card display config.
      const cdFields = sRes.data?.calendar_display?.fields;
      if (Array.isArray(cdFields) && cdFields.length >= 1 && cdFields.length <= 5) {
        setCalendarFields(cdFields);
      }
      setPaymentTypes(ptRes.data || []);
      const map = {};
      (custRes.data || []).forEach((c) => { map[c.id] = c.tags || []; });
      setCustomerTagsByCustomer(map);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [view, windowStart, monthGridDays, dayAnchor]);

  // Phase 36 — load tentative enquiries independently of bookings (different
  // cache rhythm, doesn't need to refire on calendar window scroll).
  const loadEnquiries = useCallback(async () => {
    setEnquiriesLoading(true);
    try {
      const { data } = await api.get("/booking-enquiries", { params: { status: "pending" } });
      setEnquiries(data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setEnquiriesLoading(false);
    }
  }, []);

  useEffect(() => { loadEnquiries(); }, [loadEnquiries]);

  useEffect(() => { load(); }, [load]);

  // Phase 20 — poll unread chat counts every 30s. Light, no spinner UI.
  useEffect(() => {
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const { data } = await api.get("/bookings/unread-count");
        if (!cancelled) setUnreadByBooking(data || {});
      } catch { /* swallow */ }
    };
    fetchCounts();
    const t = setInterval(fetchCounts, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const dayBookings = (d) => {
    let list = filteredBookings.filter((b) => isSameDay(new Date(b.scheduled_start), d));
    // Phase F — "My cash only" filter applies on top of any other UI filters.
    if (myCashOnly && manager?.id) {
      return list.filter((b) => b.collector_user_id === manager.id);
    }
    return list;
  };
  // Phase 36 — match enquiries with a tentative time to their day cell.
  const dayEnquiries = (d) => enquiries.filter((e) => e.tentative_start && isSameDay(new Date(e.tentative_start), d));
  const today = new Date(); today.setHours(0, 0, 0, 0);

  // Phase 36 — open the standard BookingForm pre-populated by enquiry id.
  const handleConfirmEnquiry = (enquiry) => {
    navigate(`/bookings/new?enquiryId=${enquiry.id}`);
  };
  const handleEditEnquiry = (enquiry) => {
    setEditingEnquiry(enquiry);
    setEnquiryDialogOpen(true);
  };
  const handleNewEnquiry = () => {
    setEditingEnquiry(null);
    setEnquiryDialogOpen(true);
  };

  // Phase 48 — drag-drop reschedule conflict check. Sequential modals:
  // ConflictWarningDialog first (if any conflicts) → RescheduleDialog
  // only after the manager clicks "Save anyway". Skips the network
  // round-trip entirely when the setting is disabled.
  const attemptRescheduleMove = async (booking, newDate) => {
    const old = new Date(booking.scheduled_start);
    const next = new Date(newDate);
    next.setHours(old.getHours(), old.getMinutes(), old.getSeconds(), old.getMilliseconds());
    if (!warnOnTimeConflicts) {
      setPendingMove({ booking, newDate });
      return;
    }
    try {
      const { data: check } = await api.post("/bookings/check-conflicts", {
        staff_id: booking.staff_id || null,
        customer_id: booking.customer_id || null,
        scheduled_start: next.toISOString(),
        duration_minutes: booking.scheduled_duration_minutes || 60,
        location: booking.location || null,
        booking_id_to_exclude: booking.id,
      });
      if (check?.settings_enabled && (check.conflicts || []).length > 0) {
        setMoveConflicts(check.conflicts);
        setMoveConflictPending({ booking, newDate });
        return;
      }
    } catch { /* non-fatal — fall through to the regular reschedule flow */ }
    setPendingMove({ booking, newDate });
  };

  const shift = (delta) => setWindowStart((s) => {
    const d = new Date(s);
    d.setDate(d.getDate() + delta);
    return d;
  });
  const shiftMonth = (delta) => setMonthAnchor(({ year, month }) => {
    const d = new Date(year, month + delta, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const jumpToToday = () => {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    if (view === "week") setWindowStart(d);
    else if (view === "day") setDayAnchor(d);
    else { setMonthAnchor({ year: d.getFullYear(), month: d.getMonth() }); setMonthSelectedDay(d); }
  };
  // Phase P.12 — Day view helpers.
  const shiftDay = (delta) => setDayAnchor((s) => {
    const d = new Date(s);
    d.setDate(d.getDate() + delta);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const jumpToDay = (d) => {
    const nd = new Date(d);
    nd.setHours(0, 0, 0, 0);
    setDayAnchor(nd);
    setView("day");
    // Reflect in URL so refresh/reload lands back on the same day.
    try {
      const p = new URLSearchParams(searchParams);
      p.set("view", "day");
      p.set("date", `${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, "0")}-${String(nd.getDate()).padStart(2, "0")}`);
      setSearchParams(p);
    } catch { /* noop */ }
  };
  // Phase P.12 — accept `?date=YYYY-MM-DD` deep-link once on mount so
  // shared URLs land on the intended day/week.
  useEffect(() => {
    const dstr = searchParams.get("date");
    if (!dstr) return;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dstr);
    if (!m) return;
    const [, y, mo, dd] = m;
    const parsed = new Date(Number(y), Number(mo) - 1, Number(dd));
    parsed.setHours(0, 0, 0, 0);
    if (Number.isNaN(parsed.getTime())) return;
    setDayAnchor(parsed);
    setWindowStart(parsed);
    setMonthAnchor({ year: parsed.getFullYear(), month: parsed.getMonth() });
    setMonthSelectedDay(parsed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const quickCreateForDate = (d) => {
    // Phase 45 — tapping any day cell now opens the day-detail drawer
    // instead of navigating to the create-booking form. The drawer's
    // own "+ Add booking" button is the canonical creation path.
    openDrawerForDate(d);
  };

  // Phase P.27 — calendar block/note helpers. All go through the shared
  // `load()` refresh so drag/undo/etc. picks up the mutations without an
  // extra fetch loop.
  const _ymdOf = (d) => {
    const dt = d instanceof Date ? d : new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  };
  const blocksByDate = useMemo(() => {
    const m = new Map();
    (calendarBlocks || []).forEach((b) => { if (b?.date) m.set(b.date, b); });
    return m;
  }, [calendarBlocks]);
  const notesByDate = useMemo(() => {
    const m = new Map();
    (calendarNotes || []).forEach((n) => {
      if (!n?.at) return;
      const key = String(n.at).slice(0, 10);
      const arr = m.get(key) || [];
      arr.push(n);
      m.set(key, arr);
    });
    return m;
  }, [calendarNotes]);
  const openBlockDayModal = (d) => {
    const key = _ymdOf(d);
    setBlockDayModal({ date: new Date(d), existing: blocksByDate.get(key) || null });
  };
  const saveBlockDay = async ({ dateStr, reason }) => {
    try {
      await api.post("/calendar-blocks", { date: dateStr, reason });
      toast.success("Day blocked");
      setBlockDayModal(null);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const unblockDayById = async (blockId) => {
    try {
      await api.delete(`/calendar-blocks/${blockId}`);
      toast.success("Day unblocked");
      setBlockDayModal(null);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const openNoteModal = (payload) => {
    // payload = {at: Date, editing?: note}
    setNotePickerModal(payload);
  };
  const saveNote = async ({ at, note, existingId }) => {
    try {
      if (existingId) {
        await api.put(`/calendar-notes/${existingId}`, { at, note });
        toast.success("Note updated");
      } else {
        await api.post("/calendar-notes", { at, note });
        toast.success("Note added");
      }
      setNotePickerModal(null);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/calendar-notes/${noteId}`);
      toast.success("Note deleted");
      setNotePickerModal(null);
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  // Phase 45 — legacy quickCreateDate effect retained as a no-op so the
  // ?date= deep-link path (still used by external links) keeps working.
  useEffect(() => {
    if (!quickCreateDate) return;
    openDrawerForDate(quickCreateDate);
    setQuickCreateDate(null);
  }, [quickCreateDate]);

  return (
    <div className="page" data-testid="calendar-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">
            // {view === "week" ? "7-day rolling" : monthLabel}
          </div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2">Calendar</h1>
          <div className="mono text-xs uppercase tracking-widest text-muted-foreground mt-1">
            {view === "week"
              ? `${days[0].toLocaleDateString()} – ${days[6].toLocaleDateString()}`
              : monthLabel}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="hidden md:inline-flex items-center bg-secondary rounded-md p-0.5 border border-border"
            role="tablist"
            data-testid="calendar-view-toggle"
          >
            <button
              type="button"
              onClick={() => setView("day")}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded transition-colors ${
                view === "day" ? "bg-card text-foreground shadow" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="calendar-view-day"
            >Day</button>
            <button
              type="button"
              onClick={() => setView("week")}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded transition-colors ${
                view === "week" ? "bg-card text-foreground shadow" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="calendar-view-week"
            >Week</button>
            <button
              type="button"
              onClick={() => setView("month")}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded transition-colors ${
                view === "month" ? "bg-card text-foreground shadow" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="calendar-view-month"
            >Month</button>
          </div>
          <Button variant="outline" size="icon" onClick={() => {
            if (view === "day") shiftDay(-1);
            else if (view === "week") shift(-7);
            else shiftMonth(-1);
          }} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={jumpToToday} data-testid="cal-today">Today</Button>
          <Button variant="outline" size="icon" onClick={() => {
            if (view === "day") shiftDay(1);
            else if (view === "week") shift(7);
            else shiftMonth(1);
          }} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button onClick={() => navigate("/bookings/new")} className="btn-cta gap-2" data-testid="new-booking-button">
            <Plus className="h-4 w-4" /> New booking
          </Button>
          <PageRefreshButton onRefresh={load} testId="page-refresh-btn" />
          {/* Phase K — staff filter chip + show-free toggle. */}
          <CalendarStaffFilterChip
            staffList={staffList}
            value={staffFilterId}
            onChange={setStaffFilter}
            counts={staffBookingCounts}
            showFreeOnly={staffShowFreeOnly}
            onShowFreeChange={setStaffShowFreeOnly}
          />
          {/* Phase F — "My cash only" toggle. Visible only when the
              current manager has at least one Paid Method linked to them. */}
          {hasMyCashLink && (
            <button
              type="button"
              onClick={() => setMyCashOnly((v) => !v)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-bold uppercase tracking-wider transition-colors ${
                myCashOnly
                  ? "bg-amber-100 border-amber-300 text-amber-900"
                  : "bg-card border-border text-muted-foreground hover:text-foreground"
              }`}
              data-testid="calendar-my-cash-toggle"
              aria-pressed={myCashOnly}
              title={myCashOnly ? "Showing only your cash jobs" : "Show only jobs paid through your linked Paid Methods"}
            >
              <Wallet className="h-3.5 w-3.5" /> My cash only
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm mt-6">Loading…</div>
      ) : (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
          <div className="min-w-0">
            {/* Phase P.12 — Desktop redesigned views (md+). Mobile keeps
                the legacy 7-day list rendered below in the md:hidden branch. */}
            <div className="hidden md:block">
              {view === "day" && (
                <CalendarDayView
                  date={dayAnchor}
                  bookings={filteredBookings}
                  isToday={
                    dayAnchor.getFullYear() === today.getFullYear()
                    && dayAnchor.getMonth() === today.getMonth()
                    && dayAnchor.getDate() === today.getDate()
                  }
                  onPrev={() => shiftDay(-1)}
                  onNext={() => shiftDay(1)}
                  onToday={jumpToToday}
                  onBookingClick={(id) => setOpenId(id)}
                  onSlotClick={(slot) => {
                    setQuickBookingInitialDate(slot);
                    setQuickBookingOpen(true);
                  }}
                  blocksByDate={blocksByDate}
                  notesForDate={notesByDate.get(_ymdOf(dayAnchor)) || []}
                  onBlockDay={openBlockDayModal}
                  onAddNote={openNoteModal}
                  onNoteClick={(n) => openNoteModal({ at: new Date(n.at), editing: n })}
                />
              )}
              {view === "week" && (
                <CalendarWeekView
                  days={days}
                  bookings={filteredBookings}
                  onPrev={() => shift(-7)}
                  onNext={() => shift(7)}
                  onToday={jumpToToday}
                  onBookingClick={(id) => setOpenId(id)}
                  onSlotClick={(slot) => {
                    setQuickBookingInitialDate(slot);
                    setQuickBookingOpen(true);
                  }}
                  onBookingUpdated={load}
                  weekLabel={`${days[0].toLocaleDateString()} – ${days[6].toLocaleDateString()}`}
                  todayYmd={`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`}
                  blocksByDate={blocksByDate}
                  notesByDate={notesByDate}
                  onBlockDay={openBlockDayModal}
                  onNoteClick={(n) => openNoteModal({ at: new Date(n.at), editing: n })}
                />
              )}
              {view === "month" && (
                <CalendarMonthView
                  monthGridDays={monthGridDays}
                  monthAnchor={monthAnchor}
                  bookings={filteredBookings}
                  onPrev={() => shiftMonth(-1)}
                  onNext={() => shiftMonth(1)}
                  onToday={jumpToToday}
                  onBookingClick={(id) => setOpenId(id)}
                  onJumpToDay={jumpToDay}
                  onEmptyCellClick={(d) => {
                    const slot = new Date(d);
                    slot.setHours(9, 0, 0, 0);
                    setQuickBookingInitialDate(slot);
                    setQuickBookingOpen(true);
                  }}
                  todayYmd={`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`}
                  monthLabel={monthLabel}
                  blocksByDate={blocksByDate}
                  notesByDate={notesByDate}
                  onBlockDay={openBlockDayModal}
                />
              )}
            </div>

            {/* Legacy mobile / small-screen rendering (< md). Untouched. */}
            <div className="md:hidden">
            {view === "month" ? (
              <MonthView
                monthGridDays={monthGridDays}
                monthAnchor={monthAnchor}
                bookings={filteredBookings}
                dayBookings={dayBookings}
                dayEnquiries={dayEnquiries}
                customerTagsByCustomer={customerTagsByCustomer}
                unreadByBooking={unreadByBooking}
                today={today}
                onOpenBooking={(id) => setOpenId(id)}
                onEmptyCellClick={quickCreateForDate}
                monthSelectedDay={monthSelectedDay}
                setMonthSelectedDay={setMonthSelectedDay}
                onEditEnquiry={handleEditEnquiry}
                heatmapGreenMax={heatmapGreenMax}
                heatmapAmberMax={heatmapAmberMax}
                calendarFields={calendarFields}
              />
            ) : (
              <div className="">
                {/* Mobile vertical list (≤640px) — Phase 45: gradient header per day */}
          <div className="space-y-4 sm:hidden">
            {days.map((d, i) => {
              const list = dayBookings(d);
              const dayEnqs = dayEnquiries(d);
              const isToday = isSameDay(d, today);
              const grad = dayHeaderGradient(d, today);
              return (
                <div key={i} data-testid={`day-section-${i}`}
                     onClick={(e) => {
                       if (e.target.closest && e.target.closest("[data-booking-card=true]")) return;
                       if (e.target.closest && e.target.closest("[data-day-header=true]")) return;
                       openDrawerForDate(d);
                     }}>
                  <div
                    data-day-header="true"
                    onClick={(e) => { e.stopPropagation(); openDrawerForDate(d); }}
                    className={`mb-2 rounded-md overflow-hidden bg-gradient-to-r ${grad} shadow-sm cursor-pointer`}
                    data-testid={`day-section-header-${i}`}
                  >
                    <div className="flex items-baseline justify-between px-3 py-2 text-white">
                      <div className="text-sm font-extrabold tracking-wide mono uppercase">
                        {d.toLocaleDateString(undefined, { weekday: "long" })}
                        <span className="font-normal opacity-80 mx-1.5">·</span>
                        <span className="font-extrabold">{d.toLocaleDateString(undefined, { day: "2-digit", month: "short" })}</span>
                      </div>
                      {isToday && (
                        <span className="text-[9px] font-extrabold mono uppercase tracking-[0.2em] bg-white/95 text-fuchsia-700 rounded-full px-2 py-0.5 shadow-sm">
                          TODAY
                        </span>
                      )}
                    </div>
                  </div>
                  {list.length === 0 && dayEnqs.length === 0 ? (
                    <div className="text-xs text-muted-foreground italic px-3 pb-2">No bookings</div>
                  ) : (
                    <div className="space-y-2 px-1">
                      {dayEnqs
                        .sort((a, b) => new Date(a.tentative_start) - new Date(b.tentative_start))
                        .map((enq) => (
                          <EnquiryTile key={`enq-${enq.id}`} enquiry={enq} onClick={() => handleEditEnquiry(enq)} />
                        ))}
                      {(() => {
                        const sorted = [...list].sort((a, b) => new Date(a.scheduled_start) - new Date(b.scheduled_start));
                        const prevByStaff = {};
                        const out = [];
                        for (const b of sorted) {
                          const prev = prevByStaff[b.staff_id] || null;
                          if (b.travel_from_previous) {
                            out.push(<TravelPill key={`tp-${b.id}`} travel={b.travel_from_previous} prevBooking={prev} nextBooking={b} />);
                          }
                          out.push(
                            <ConfigurableBookingTile
                              key={b.id}
                              b={b}
                              fields={calendarFields}
                              density="full"
                              onClick={() => setOpenId(b.id)}
                              customerTags={customerTagsByCustomer[b.customer_id] || []}
                              unreadCount={unreadByBooking[b.id] || 0}
                            />
                          );
                          prevByStaff[b.staff_id] = b;
                        }
                        return out;
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Desktop grid (≥641px) — Phase 45 redesign: clean compact
              tiles + gradient day headers. Tapping the column header /
              empty area opens the day-detail drawer. Drag-to-reschedule
              kept on the tiles themselves (drop target = column). */}
          <div className="hidden sm:grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-0 border border-border rounded-[8px] overflow-hidden bg-card">
            {days.map((d, i) => {
              const list = dayBookings(d);
              const dayEnqs = dayEnquiries(d);
              const isToday = isSameDay(d, today);
              const dragActive = dragOverIdx === i;
              const isDragging = draggingId !== null;
              const isLast = i === days.length - 1;
              const grad = dayHeaderGradient(d, today);
              return (
                <div
                  key={i}
                  className={`p-1.5 min-h-[320px] min-w-0 overflow-hidden transition-colors cursor-pointer ${
                    !isLast ? "lg:border-r-2 md:border-r-2 sm:border-r-2 border-r-2 border-slate-200" : ""
                  } ${dragActive ? "bg-accent/5 ring-2 ring-accent/30 ring-inset" : ""}`}
                  data-testid={`cal-day-col-${i}`}
                  onClick={(e) => {
                    // Phase 45 — empty / header click opens the day-detail
                    // drawer (not the create-booking form). Tile clicks
                    // stopPropagation so they still open BookingDetail.
                    if (e.defaultPrevented) return;
                    if (e.target.closest && e.target.closest("[data-booking-card=true]")) return;
                    openDrawerForDate(d);
                  }}
                  onDragOver={(e) => {
                    if (!isDragging) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    if (dragOverIdx !== i) setDragOverIdx(i);
                  }}
                  onDragLeave={() => { if (dragOverIdx === i) setDragOverIdx(null); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOverIdx(null);
                    const id = e.dataTransfer.getData("text/booking-id") || draggingId;
                    setDraggingId(null);
                    if (!id) return;
                    const booking = bookings.find((bk) => bk.id === id);
                    if (!booking) return;
                    if (isSameDay(new Date(booking.scheduled_start), d)) return;
                    // Phase 48 — sequential conflict check BEFORE
                    // RescheduleDialog. Never stack the two modals.
                    attemptRescheduleMove(booking, d);
                  }}
                >
                  <div
                    className={`sticky top-0 z-10 -mx-1.5 px-2 pt-1.5 pb-1.5 mb-2 bg-gradient-to-r ${grad} text-white shadow-sm`}
                    data-testid={`cal-day-header-${i}`}
                  >
                    <div className="flex items-baseline justify-between gap-1">
                      <div className="text-[11px] font-extrabold uppercase tracking-wide">
                        {d.toLocaleDateString(undefined, { weekday: "short" })}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="text-[11px] tabular-nums font-bold">
                          {d.toLocaleDateString(undefined, { day: "2-digit", month: "short" })}
                        </div>
                        {isToday && (
                          <span className="text-[8px] font-extrabold mono uppercase tracking-[0.15em] bg-white/95 text-fuchsia-700 rounded-full px-1.5 py-[1px] shadow-sm"
                                data-testid={`cal-day-today-pill-${i}`}>
                            TODAY
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1" data-testid={`cal-day-col-body-${i}`}>
                    {list.length === 0 && dayEnqs.length === 0 ? (
                      <div className="text-[11px] text-muted-foreground italic select-none py-3 text-center" data-testid={`cal-day-empty-${i}`}>
                        No bookings
                      </div>
                    ) : (
                      <>
                        {dayEnqs
                          .sort((a, b) => new Date(a.tentative_start) - new Date(b.tentative_start))
                          .map((enq) => (
                            <EnquiryTile key={`enq-${enq.id}`} enquiry={enq} onClick={() => handleEditEnquiry(enq)} />
                          ))}
                        {(() => {
                          const sorted = [...list].sort((a, b) => new Date(a.scheduled_start) - new Date(b.scheduled_start));
                          const prevByStaff = {};
                          const out = [];
                          for (const b of sorted) {
                            const prev = prevByStaff[b.staff_id] || null;
                            if (b.travel_from_previous && prev) {
                              out.push(<TravelPill key={`tp-${b.id}`} travel={b.travel_from_previous} prevBooking={prev} nextBooking={b} />);
                            }
                            out.push(
                              <ConfigurableBookingTile
                                key={b.id}
                                b={b}
                                fields={calendarFields}
                                onClick={() => setOpenId(b.id)}
                                customerTags={customerTagsByCustomer[b.customer_id] || []}
                                unreadCount={unreadByBooking[b.id] || 0}
                                draggable
                                dragging={draggingId === b.id}
                                onDragStart={(e) => {
                                  setDraggingId(b.id);
                                  e.dataTransfer.effectAllowed = "move";
                                  e.dataTransfer.setData("text/booking-id", b.id);
                                }}
                                onDragEnd={() => { setDraggingId(null); setDragOverIdx(null); }}
                              />
                            );
                            prevByStaff[b.staff_id] = b;
                          }
                          return out;
                        })()}
                      </>
                    )}
                  </div>
                  {/* Phase B — subtle hint when the day column has more
                      bookings than fit in the viewport-bound area. The
                      inner div above scrolls; this hint signals it. */}
                  {(list.length + dayEnqs.length) > 5 && (
                    <div
                      className="mt-1 flex items-center justify-center gap-1 text-[10px] mono uppercase tracking-wider text-muted-foreground select-none"
                      data-testid={`cal-day-scroll-hint-${i}`}
                      title={`${list.length + dayEnqs.length} bookings — scroll to view`}
                    >
                      <ChevronDown size={10} strokeWidth={2.4} aria-hidden="true" />
                      <span>{list.length + dayEnqs.length} total · scroll</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
              </div>
            )}
            </div>
          </div>
          <PendingEnquiriesPanel
            enquiries={enquiries}
            loading={enquiriesLoading}
            onEdit={handleEditEnquiry}
            onConfirm={handleConfirmEnquiry}
            onChanged={loadEnquiries}
          />
        </div>
      )}

      <Sheet open={!!openId} onOpenChange={(o) => {
        if (!o) {
          setOpenId(null);
          // Phase 23 — also strip the deep-link param so reload doesn't re-open.
          if (searchParams.get("bookingId")) {
            const next = new URLSearchParams(searchParams);
            next.delete("bookingId");
            setSearchParams(next, { replace: true });
          }
        }
      }}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="booking-sheet">
          <SheetHeader>
            <SheetTitle className="sr-only">Booking detail</SheetTitle>
            <SheetDescription className="sr-only">Booking detail panel</SheetDescription>
          </SheetHeader>
          {openId && (
            <BookingDetail
              id={openId}
              currency={currency}
              paymentTypes={paymentTypes}
              customerTagsByCustomer={customerTagsByCustomer}
              requireLocation={requireLocation}
              onClose={() => setOpenId(null)}
              onChanged={load}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Phase 4: drag-to-reschedule confirmation */}
      <RescheduleDialog
        pending={pendingMove}
        busy={moveBusy}
        onCancel={() => setPendingMove(null)}
        onConfirm={async () => {
          if (!pendingMove) return;
          const { booking, newDate } = pendingMove;
          const old = new Date(booking.scheduled_start);
          const next = new Date(newDate);
          next.setHours(old.getHours(), old.getMinutes(), old.getSeconds(), old.getMilliseconds());
          setMoveBusy(true);
          try {
            await api.put(`/bookings/${booking.id}`, { scheduled_start: next.toISOString() });
            toast.success(`Rescheduled ${booking.booking_code}`);
            setPendingMove(null);
            await load();
          } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail));
          } finally {
            setMoveBusy(false);
          }
        }}
      />

      {/* Phase 48 — soft conflict warning for drag-drop reschedule.
          Shown BEFORE RescheduleDialog. On "Save anyway" we close this
          and surface the standard reschedule confirmation. */}
      <ConflictWarningDialog
        open={!!moveConflictPending && moveConflicts.length > 0}
        onOpenChange={(o) => { if (!o) { setMoveConflictPending(null); setMoveConflicts([]); } }}
        conflicts={moveConflicts}
        onProceed={() => {
          const pending = moveConflictPending;
          setMoveConflictPending(null);
          setMoveConflicts([]);
          if (pending) setPendingMove(pending);
        }}
        onCancel={() => { setMoveConflictPending(null); setMoveConflicts([]); }}
      />

      {/* Phase 36 + 41 — stacked Floating Action Buttons. Primary "+ Booking"
          (Phase 41) sits above the original "+ Enquiry" so the most common
          on-the-road action (confirmed booking from a phone call) is the
          easiest tap. On narrow viewports both shrink to icon-only chips.
          Phase J — pushed up to `bottom-24` on mobile so the stack clears
          the fixed bottom nav (the "+ Enquiry" pill was overlapping the
          Jobs button at `bottom-6`). Desktop keeps the original 24px gap. */}
      <div className="fixed z-40 bottom-24 sm:bottom-6 right-6 flex flex-col items-end gap-3"
           data-testid="calendar-fab-stack">
        <button
          type="button"
          onClick={() => setQuickBookingOpen(true)}
          className="inline-flex items-center gap-2 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground px-5 py-3 shadow-lg ring-2 ring-accent/30 hover:ring-accent/50 transition-all font-bold text-sm"
          data-testid="quick-booking-fab"
          aria-label="New quick booking"
        >
          <CalendarPlus className="h-5 w-5" />
          <span className="hidden sm:inline">+ Booking</span>
          <span className="sm:hidden">Booking</span>
        </button>
        <button
          type="button"
          onClick={handleNewEnquiry}
          className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-600 text-white px-5 py-3 shadow-lg ring-2 ring-amber-300/50 hover:ring-amber-300 transition-all font-bold text-sm"
          data-testid="new-enquiry-fab"
          aria-label="New enquiry / note"
        >
          <Hourglass className="h-4 w-4" />
          <span className="hidden sm:inline">+ Note / Enquiry</span>
          <span className="sm:hidden">+ Enquiry</span>
        </button>
      </div>
      <EnquiryDialog
        open={enquiryDialogOpen}
        onOpenChange={(o) => { setEnquiryDialogOpen(o); if (!o) { setEditingEnquiry(null); setEnquiryInitialDate(null); } }}
        mode={editingEnquiry ? "edit" : "create"}
        initial={editingEnquiry}
        initialDate={enquiryInitialDate}
        onSaved={() => { loadEnquiries(); }}
      />
      <QuickBookingForm
        open={quickBookingOpen}
        onOpenChange={(o) => { setQuickBookingOpen(o); if (!o) setQuickBookingInitialDate(null); }}
        initialDate={quickBookingInitialDate}
        onCreated={() => { load(); }}
        calendarBlocks={calendarBlocks}
      />

      {/* Phase P.27 — day-block modal + note picker. Both share a compact
          shadcn-style panel; no new Radix Dialog dep needed. */}
      <CalendarBlockDayDialog
        state={blockDayModal}
        onClose={() => setBlockDayModal(null)}
        onSave={saveBlockDay}
        onUnblock={unblockDayById}
      />
      <CalendarNoteDialog
        state={notePickerModal}
        onClose={() => setNotePickerModal(null)}
        onSave={saveNote}
        onDelete={deleteNote}
      />

      {/* Phase 45 — Day-detail drawer. Triggered by tapping any day on
          7-day or month view. The drawer is the single canonical jumping
          point for creating bookings/enquiries from the calendar. */}
      <DayDetailDrawer
        open={!!drawerDate}
        onOpenChange={(o) => { if (!o) setDrawerDate(null); }}
        date={drawerDate}
        bookings={filteredBookings}
        enquiries={enquiries}
        onOpenBooking={(id) => { setDrawerDate(null); setOpenId(id); }}
        onPrevDay={() => drawerDate && openDrawerForDate(new Date(drawerDate.getTime() - 86400000))}
        onNextDay={() => drawerDate && openDrawerForDate(new Date(drawerDate.getTime() + 86400000))}
        onAddBooking={() => {
          const d = drawerDate ? new Date(drawerDate) : new Date();
          d.setHours(9, 0, 0, 0);
          setQuickBookingInitialDate(d);
          setDrawerDate(null);
          setQuickBookingOpen(true);
        }}
        onAddEnquiry={() => {
          const d = drawerDate ? new Date(drawerDate) : new Date();
          setEnquiryInitialDate(d);
          setEditingEnquiry(null);
          setDrawerDate(null);
          setEnquiryDialogOpen(true);
        }}
        onEditEnquiry={(enq) => { setDrawerDate(null); handleEditEnquiry(enq); }}
      />
    </div>
  );
}

function RescheduleDialog({ pending, busy, onCancel, onConfirm }) {
  const open = !!pending;
  if (!open) return null;
  const { booking, newDate } = pending;
  const oldDate = new Date(booking.scheduled_start);
  const next = new Date(newDate);
  next.setHours(oldDate.getHours(), oldDate.getMinutes(), 0, 0);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onCancel()}>
      <DialogContent data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle>Reschedule {booking.booking_code}?</DialogTitle>
          <DialogDescription>
            Move this booking — the time-of-day stays the same. Travel will be recomputed in the background.
          </DialogDescription>
        </DialogHeader>
        <div className="bg-card border border-border rounded-[6px] p-3 space-y-2">
          <div className="text-sm">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">From</div>
            <div className="font-bold mono">{oldDate.toLocaleString()}</div>
          </div>
          <div className="text-sm">
            <div className="mono text-[10px] uppercase tracking-widest text-accent">To</div>
            <div className="font-bold mono text-accent">{next.toLocaleString()}</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="reschedule-cancel">Cancel</Button>
          <Button onClick={onConfirm} disabled={busy} className="btn-cta" data-testid="reschedule-confirm">
            {busy ? "Moving…" : "Confirm reschedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Phase 7 — Month view ----------
const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function MonthView({
  monthGridDays, monthAnchor, dayBookings, dayEnquiries = () => [], customerTagsByCustomer, unreadByBooking = {}, today,
  onOpenBooking, onEmptyCellClick, monthSelectedDay, setMonthSelectedDay, onEditEnquiry,
  calendarFields = ["customer_name", "job_template", "time_start", "payment_type", "payment_status"],
  heatmapGreenMax = 3, heatmapAmberMax = 5,
}) {
  const { month } = monthAnchor;
  const selectedList = dayBookings(monthSelectedDay);

  return (
    <div className="mt-6" data-testid="month-view">
      {/* Desktop 6x7 grid — Phase 45: glanceable cells with day number +
          count badge + dominant payment dot. No individual tiles. Tap
          opens the day-detail drawer. Today gets the indigo→fuchsia
          gradient strip across the top of its cell. */}
      <div className="hidden sm:block bg-card border border-border rounded-[8px] overflow-hidden">
        <div className="grid grid-cols-7 border-b-2 border-slate-200">
          {WEEKDAY_LABELS.map((w) => (
            <div key={w} className="px-3 py-2 text-xs font-extrabold uppercase tracking-wide text-muted-foreground text-center">
              {w}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 grid-rows-6">
          {monthGridDays.map((d, i) => {
            const list = dayBookings(d);
            const dayEnqs = dayEnquiries(d);
            const inMonth = d.getMonth() === month;
            const isToday = isSameDay(d, today);
            const dCopy = new Date(d); dCopy.setHours(0, 0, 0, 0);
            const tCopy = new Date(today); tCopy.setHours(0, 0, 0, 0);
            const isPast = dCopy < tCopy && !isToday;
            const isLastRow = i >= 35;
            const isLastCol = (i + 1) % 7 === 0;
            const dayKey = ymdKey(d);
            return (
              <button
                type="button"
                key={i}
                className={`relative min-h-[110px] p-2 text-left transition-colors group ${
                  !isLastRow ? "border-b-2 border-slate-200" : ""
                } ${!isLastCol ? "border-r-2 border-slate-200" : ""} ${
                  !inMonth ? "bg-muted/30 opacity-60" : isPast ? "bg-card/95 opacity-90" : "bg-card"
                } hover:bg-secondary/40`}
                data-testid={`month-cell-${i}`}
                onClick={() => onEmptyCellClick(d)}
              >
                {isToday && (
                  <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-600 via-purple-600 to-fuchsia-500"
                       aria-hidden="true" />
                )}
                <div className="flex items-start justify-between gap-1.5">
                  <div className="flex items-baseline gap-1.5">
                    <span className={`text-xl font-extrabold leading-none ${
                      !inMonth ? "text-muted-foreground/60" : isToday ? "text-fuchsia-700" : isPast ? "text-muted-foreground" : "text-foreground"
                    }`} data-testid={`month-cell-${i}-day-num`}>
                      {d.getDate()}
                    </span>
                    {isToday && (
                      <span className="mono text-[8px] uppercase tracking-[0.2em] font-extrabold bg-fuchsia-100 text-fuchsia-700 rounded-full px-1.5 py-[1px]">
                        TODAY
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {/* Phase 52 — heat-map job count pill. On the Today
                        cell we skip the "jobs" suffix so the TODAY pill
                        + count fit comfortably without clipping.
                        Phase 53 — colour thresholds are now manager-
                        configurable per tenant. */}
                    {list.length > 0 && (
                      <span
                        className={`inline-flex items-center mono text-[10px] uppercase font-bold rounded-full px-1.5 py-0.5 whitespace-nowrap shrink-0 ${monthDayCountClass(list.length, heatmapGreenMax, heatmapAmberMax)}`}
                        data-testid={`month-day-count-${dayKey}`}
                        aria-label={`${list.length} job${list.length === 1 ? "" : "s"}`}
                      >
                        {list.length}
                        {!isToday && (
                          <span className="hidden md:inline">&nbsp;job{list.length === 1 ? "" : "s"}</span>
                        )}
                      </span>
                    )}
                    {dayEnqs.length > 0 && (
                      <span
                        className="mono text-[9px] uppercase tracking-widest text-amber-800 bg-amber-100 border border-amber-300 rounded-sm px-1 py-0.5 font-bold"
                        data-testid={`month-cell-${i}-pending-badge`}
                      >
                        +{dayEnqs.length} enq
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Mobile: mini-grid + selected-day list */}
      <div className="sm:hidden space-y-4">
        <div className="bg-card border border-border rounded-[8px] overflow-hidden" data-testid="month-mini-grid">
          <div className="grid grid-cols-7 border-b border-slate-200">
            {WEEKDAY_LABELS.map((w) => (
              <div key={w} className="py-1.5 text-[10px] font-extrabold uppercase tracking-wide text-muted-foreground text-center">
                {w[0]}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {monthGridDays.map((d, i) => {
              const list = dayBookings(d);
              const inMonth = d.getMonth() === month;
              const isToday = isSameDay(d, today);
              const isSelected = isSameDay(d, monthSelectedDay);
              const dayKey = ymdKey(d);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => { setMonthSelectedDay(new Date(d)); onEmptyCellClick(d); }}
                  className={`relative h-12 text-sm font-bold transition-colors ${
                    !inMonth ? "text-muted-foreground/50 opacity-70" : isSelected ? "bg-accent text-accent-foreground" : isToday ? "text-fuchsia-700" : "text-foreground"
                  } ${!isSelected && "hover:bg-secondary/60"}`}
                  data-testid={`month-mini-day-${i}`}
                >
                  {isToday && !isSelected && (
                    <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-indigo-600 via-purple-600 to-fuchsia-500" aria-hidden="true" />
                  )}
                  <span>{d.getDate()}</span>
                  {/* Phase 52 — top-right heat-map count pill (mobile: number only) */}
                  {list.length > 0 && (
                    <span
                      className={`absolute top-0.5 right-0.5 mono text-[9px] font-bold leading-none rounded-full px-1.5 py-0.5 ${monthDayCountClass(list.length, heatmapGreenMax, heatmapAmberMax)}`}
                      data-testid={`month-day-count-${dayKey}`}
                      aria-label={`${list.length} job${list.length === 1 ? "" : "s"}`}
                    >
                      {list.length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div data-testid="month-selected-day-list">
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-base font-extrabold">
              {monthSelectedDay.toLocaleDateString(undefined, { weekday: "long", day: "2-digit", month: "short" })}
            </div>
            <Button size="sm" variant="outline" onClick={() => onEmptyCellClick(monthSelectedDay)} data-testid="month-selected-add-booking">
              <Plus className="h-3 w-3 mr-1" /> Book
            </Button>
          </div>
          {selectedList.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">No bookings this day.</div>
          ) : (
            <div className="space-y-2">
              {selectedList.map((b) => (
                <ConfigurableBookingTile
                  key={b.id}
                  b={b}
                  fields={calendarFields}
                  density="full"
                  onClick={() => onOpenBooking(b.id)}
                  customerTags={customerTagsByCustomer[b.customer_id] || []}
                  unreadCount={unreadByBooking[b.id] || 0}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MonthChip({ b, tags, onClick }) {
  const { hex } = staffColorVar(b.staff_id);
  const firstTag = tags?.[0];
  const flagged = b.scope_change_flag && !b.scope_change_acknowledged;
  const shortName = bookingFirstDesc(b).slice(0, 10);
  const extra = bookingExtraLinesCount(b);
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative w-full text-left bg-secondary/60 hover:bg-secondary rounded-[4px] px-1.5 py-1 text-[11px] leading-tight truncate flex items-center gap-1.5 transition-colors"
      style={{ borderLeft: `3px solid ${hex}` }}
      data-testid={`month-chip-${b.id}`}
    >
      <span className="mono tabular-nums font-bold">{formatTime(b.scheduled_start)}</span>
      {/* Phase 22 — icon-only payment badge keeps the colour signal without
          eating tile width. Full label still on hover/tap via title + aria. */}
      <PaymentBadge job={b} size="icon" className="shrink-0" />
      <span className="truncate">{shortName}</span>
      {extra > 0 && (
        <span className="shrink-0 mono text-[9px] font-bold text-accent" data-testid={`month-chip-multi-${b.id}`}>+{extra}</span>
      )}
      {flagged && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-rose-600 shrink-0" aria-label="Scope change" />}
      {!flagged && firstTag && (
        <span
          className="ml-auto h-1.5 w-1.5 rounded-full shrink-0"
          style={{ backgroundColor: firstTag.color }}
          title={tags.map((t) => t.name).join(", ")}
        />
      )}
    </button>
  );
}

function DayOverflowPopover({ dayDate, all, tagsByCustomer, onOpenBooking }) {
  const [open, setOpen] = useState(false);
  const extra = all.length - 3;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); }}
          className="w-full text-left text-[10px] mono uppercase tracking-widest font-bold text-accent hover:underline px-1.5 py-0.5"
          data-testid={`month-cell-overflow-${dayDate.getDate()}`}
        >
          +{extra} more
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start" onClick={(e) => e.stopPropagation()}>
        <div className="px-3 py-2 border-b border-border bg-secondary/40">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-accent">// {dayDate.toLocaleDateString()}</div>
          <div className="text-sm font-bold mt-0.5">{all.length} bookings</div>
        </div>
        <div className="max-h-80 overflow-y-auto divide-y divide-border">
          {all.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => { setOpen(false); onOpenBooking(b.id); }}
              className="w-full text-left px-3 py-2 hover:bg-secondary/60"
              data-testid={`month-overflow-item-${b.id}`}
            >
              <div className="flex items-center gap-2">
                <span className="mono tabular-nums font-bold text-sm">{formatTime(b.scheduled_start)}</span>
                <span className="text-sm truncate flex-1">{b.customer_name}</span>
              </div>
              <div className="text-[11px] text-muted-foreground truncate">
                {bookingFirstDesc(b)}{bookingExtraLinesCount(b) > 0 ? ` +${bookingExtraLinesCount(b)}` : ""}
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}


// Phase K — Staff filter chip with popover. Reuses the Phase J compact-
// chip aesthetic. Selection persists via URL (?staff=<id|all>) so it
// survives refreshes and is shareable. "Show free only" is a UI hint
// (it doesn't currently mutate the bookings list — staff selection alone
// already reveals empty days; the toggle telegraphs intent to the user).
function CalendarStaffFilterChip({ staffList, value, onChange, counts, showFreeOnly, onShowFreeChange }) {
  const [open, setOpen] = useState(false);
  const selected = (value || "all").toLowerCase();
  const selectedStaff = selected === "all" ? null : staffList.find((s) => s.id === selected);
  const chipLabel = selectedStaff ? selectedStaff.name : "All";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-bold transition-colors ${selectedStaff ? "bg-sky-50 border-sky-300 text-sky-900" : "bg-card border-border text-muted-foreground hover:text-foreground"}`}
          data-testid="calendar-staff-filter-chip"
          aria-pressed={!!selectedStaff}
        >
          <Users className="h-3.5 w-3.5" />
          <span className="mono uppercase tracking-wider text-[10px]">Staff:</span>
          <span className="font-extrabold">{chipLabel}</span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-0"
        align="start"
        data-testid="calendar-staff-filter-popover"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-2 border-b border-border">
          <button
            type="button"
            onClick={() => { onChange("all"); setOpen(false); }}
            className={`w-full text-left px-2 py-1.5 rounded text-xs hover:bg-secondary/60 transition-colors flex items-center justify-between ${selected === "all" ? "font-extrabold text-foreground" : "text-muted-foreground"}`}
            data-testid="calendar-staff-option-all"
          >
            <span>All staff</span>
            {selected === "all" && <CheckCircle2 className="h-3.5 w-3.5 text-sky-600" />}
          </button>
        </div>
        <div className="max-h-64 overflow-y-auto p-2 space-y-0.5">
          {staffList.length === 0 && (
            <div className="text-[11px] italic text-muted-foreground px-2 py-1.5">No active staff.</div>
          )}
          {staffList.map((s) => {
            const isSel = s.id === selected;
            const n = counts?.[s.id] || 0;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => { onChange(s.id); setOpen(false); }}
                className={`w-full text-left px-2 py-1.5 rounded text-xs hover:bg-secondary/60 transition-colors flex items-center justify-between gap-2 ${isSel ? "font-extrabold text-foreground bg-sky-50" : "text-muted-foreground"}`}
                data-testid={`calendar-staff-option-${s.id}`}
              >
                <span className="truncate">{s.name}</span>
                <span className="flex items-center gap-1.5 shrink-0">
                  <span className={`mono text-[10px] tabular-nums px-1.5 py-0.5 rounded ${n > 0 ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-900"}`}>
                    {n}
                  </span>
                  {isSel && <CheckCircle2 className="h-3.5 w-3.5 text-sky-600" />}
                </span>
              </button>
            );
          })}
        </div>
        <div className="border-t border-border p-2 flex items-center gap-2">
          <input
            id="cal-staff-free-toggle"
            type="checkbox"
            checked={!!showFreeOnly}
            onChange={(e) => onShowFreeChange(e.target.checked)}
            className="h-3.5 w-3.5"
            data-testid="calendar-staff-show-free-toggle"
          />
          <label htmlFor="cal-staff-free-toggle" className="text-[11px] text-muted-foreground select-none cursor-pointer">
            Show free only (empty days)
          </label>
        </div>
      </PopoverContent>
    </Popover>
  );
}


// ---------- Phase P.27 — Block Day + Calendar Note dialogs ----------
// Kept inline so this feature ships as one file diff. Both dialogs use a
// simple fixed-overlay pattern to avoid pulling in a heavier Radix Dialog
// nesting story (the Calendar page already wrangles several popovers).
function CalendarBlockDayDialog({ state, onClose, onSave, onUnblock }) {
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (state) setReason(state.existing?.reason || "");
  }, [state]);
  if (!state) return null;
  const dateStr = `${state.date.getFullYear()}-${String(state.date.getMonth() + 1).padStart(2, "0")}-${String(state.date.getDate()).padStart(2, "0")}`;
  const humanDate = state.date.toLocaleDateString(undefined, { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  return (
    <div className="fixed inset-0 z-[65] bg-black/50 grid place-items-center p-4" role="dialog" aria-modal="true" data-testid="calendar-block-day-modal">
      <div className="bg-card border border-border rounded-[10px] w-full max-w-md p-5 space-y-4 shadow-2xl">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-accent">// Block calendar day</div>
          <h3 className="text-lg font-extrabold tracking-tight mt-1">{state.existing ? "Update blocked day" : "Block this day"}</h3>
          <p className="text-xs text-muted-foreground mt-1">{humanDate}</p>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reason</label>
          <input
            className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            placeholder="Bank holiday, Personal day off…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="calendar-block-day-reason-input"
          />
        </div>
        <div className="flex items-center justify-between gap-2 pt-1">
          <div>
            {state.existing && (
              <Button
                variant="outline"
                size="sm"
                className="border-destructive/40 text-destructive hover:bg-destructive/10"
                onClick={() => onUnblock(state.existing.id)}
                data-testid="calendar-block-day-remove-btn"
              >
                Unblock
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose} data-testid="calendar-block-day-cancel-btn">Cancel</Button>
            <Button size="sm" className="btn-cta" onClick={() => onSave({ dateStr, reason: reason.trim() })} data-testid="calendar-block-day-save-btn">
              {state.existing ? "Update" : "Block day"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CalendarNoteDialog({ state, onClose, onSave, onDelete }) {
  const [text, setText] = useState("");
  const [hh, setHh] = useState("09");
  const [mm, setMm] = useState("00");
  useEffect(() => {
    if (!state) return;
    const at = state.editing ? new Date(state.editing.at) : new Date(state.at);
    setText(state.editing?.note || "");
    setHh(String(at.getHours()).padStart(2, "0"));
    setMm(String(at.getMinutes()).padStart(2, "0"));
  }, [state]);
  if (!state) return null;
  const baseDate = state.editing ? new Date(state.editing.at) : new Date(state.at);
  const humanDate = baseDate.toLocaleDateString(undefined, { weekday: "long", day: "2-digit", month: "long" });
  const submit = () => {
    const d = new Date(baseDate);
    d.setHours(Number(hh || 0), Number(mm || 0), 0, 0);
    onSave({
      at: d.toISOString(),
      note: (text || "").trim(),
      existingId: state.editing?.id,
    });
  };
  return (
    <div className="fixed inset-0 z-[65] bg-black/50 grid place-items-center p-4" role="dialog" aria-modal="true" data-testid="calendar-note-modal">
      <div className="bg-card border border-border rounded-[10px] w-full max-w-md p-5 space-y-4 shadow-2xl">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-accent">// Calendar note</div>
          <h3 className="text-lg font-extrabold tracking-tight mt-1">{state.editing ? "Edit note" : "Add note"}</h3>
          <p className="text-xs text-muted-foreground mt-1">{humanDate}</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Time</label>
            <div className="mt-1 flex items-center gap-1">
              <input inputMode="numeric" maxLength={2} value={hh} onChange={(e) => setHh(e.target.value.replace(/[^0-9]/g,'').slice(0,2))} className="w-12 h-10 text-center rounded-md border border-input bg-background text-sm" data-testid="calendar-note-hour-input" />
              <span className="mono text-muted-foreground">:</span>
              <input inputMode="numeric" maxLength={2} value={mm} onChange={(e) => setMm(e.target.value.replace(/[^0-9]/g,'').slice(0,2))} className="w-12 h-10 text-center rounded-md border border-input bg-background text-sm" data-testid="calendar-note-minute-input" />
            </div>
          </div>
          <div className="flex-1">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Note</label>
            <input
              className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder="Ring customer B-000823 for payment…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              autoFocus
              data-testid="calendar-note-text-input"
            />
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 pt-1">
          <div>
            {state.editing && (
              <Button
                variant="outline"
                size="sm"
                className="border-destructive/40 text-destructive hover:bg-destructive/10"
                onClick={() => onDelete(state.editing.id)}
                data-testid="calendar-note-delete-btn"
              >
                Delete
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose} data-testid="calendar-note-cancel-btn">Cancel</Button>
            <Button size="sm" className="btn-cta" disabled={!text.trim()} onClick={submit} data-testid="calendar-note-save-btn">
              {state.editing ? "Save" : "Add note"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
